import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from 'react-query';
import { inventoryAPI, adminAPI } from '../../services/api';
import api from '../../services/api'; // Import the base api object
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiPlus, FiTrash2, FiEdit, FiCopy, FiPackage, FiBox, FiTag, FiWifi, FiUpload, FiDownload } from 'react-icons/fi';
import { LuCheck, LuTriangleAlert, LuX, LuBox, LuTruck } from 'react-icons/lu';
import { COUNTRIES, getStatesForCountry, validatePhoneNumber, formatPhoneNumber, validatePostalCode } from '../../utils/addressData';
import { toast } from 'react-toastify';

const normalizeWarehouseAddress = (warehouse) => {
  if (!warehouse) return null;

  return {
    ...warehouse,
    street_address: warehouse.street_address || warehouse.address_line1 || '',
    address_line_2: warehouse.address_line_2 || warehouse.address_line2 || '',
    city: warehouse.city || '',
    state_province: warehouse.state_province || '',
    postal_code: warehouse.postal_code || '',
    country: warehouse.country || ''
  };
};

const formatWarehouseAddress = (warehouse) => {
  if (!warehouse) return '';

  const parts = [
    warehouse.street_address,
    warehouse.address_line_2,
    warehouse.city,
    warehouse.state_province,
    warehouse.postal_code,
    warehouse.country
  ].filter(Boolean);

  return parts.join(', ');
};

const splitAssetValues = (assetString) => (
  assetString
    .split(/[\n,\t]+/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
);

const formatAssetTypeLabel = (assetType) => (
  assetType === 'serialNumbers' ? 'Serial Numbers' : 'MAC Addresses'
);

const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const cloneReceivingInstructions = (instructions = {}) => ({
  require_serial_numbers_on_receive: Boolean(instructions.require_serial_numbers_on_receive),
  require_mac_addresses_on_receive: Boolean(instructions.require_mac_addresses_on_receive),
  relabel_on_receive: Boolean(instructions.relabel_on_receive)
});

const IncomingInventoryForm = ({ formik, setSelectedDestinationWarehouseName, onValidationRequest, isAdmin = false }) => {
  const [customerWarehouses, setCustomerWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedDestinationWarehouse, setSelectedDestinationWarehouse] = useState(null);
  const [accessibleWarehouses, setAccessibleWarehouses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [shipmentType, setShipmentType] = useState(''); // 'pallets' or 'cartons'
  
  // Enhanced address form state
  const [newAddressForm, setNewAddressForm] = useState({
    name: '',
    street_address: '',
    address_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'Canada',
    contact_name: '',
    contact_phone: '',
    notes: ''
  });

  // Pallet management state
  const [pallets, setPallets] = useState([]);
  const [showAddPallet, setShowAddPallet] = useState(false);
  const [currentPallet, setCurrentPallet] = useState({
    label: '',
    dimensions: { length: '', width: '', height: '' },
    weight: '',
    declaredValue: '',
    notes: '',
    items: [], // For multi-SKU pallets
    // For single-SKU pallets
    productSku: '',
    internalSku: '',
    productName: '',
    packagingType: 'individual', // New field
    quantity: 1,
    unitOfMeasure: 'each',
    // For cartons (single-SKU)
    cartonCount: 1,
    unitsPerCarton: 1,
    // Separate dimensions for cartons and individual items
    cartonDimensions: { length: '', width: '', height: '', weight: '' },
    itemDimensions: { length: '', width: '', height: '', weight: '' },
    receivingInstructions: cloneReceivingInstructions(),
    // Consolidated dimensions field - holds either carton or individual dimensions based on packagingType
    dimensions: { length: '', width: '', height: '', weight: '' },
    serialNumbers: [],
    macAddresses: [],
    depalletizeOnArrival: false,
    crossDock: false,
    // Cross-dock specific fields
    crossDockShippingArrangement: '', // 'customer_arranges' or 'warehouse_arranges'
    crossDockDestination: {
      useExistingAddress: false,
      selectedAddressId: '',
      newAddress: {
        name: '',
        street_address: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'Canada',
        contact_name: '',
        contact_phone: '',
        notes: ''
      }
    },
    crossDockShippingDetails: {
      carrierPreference: '',
      serviceLevel: '',
      specialInstructions: ''
    },
    crossDockDocuments: {
      providedNow: false,
      bolNumber: '',
      trackingNumber: '',
      documentNotes: ''
    },
    cartonInfo: {
      hasCartons: false,
      cartonsPerPallet: 1,
      dimensions: { length: '', width: '', height: '', weight: '' }
    }
  });

  // Carton management state (for carton shipments)
  const [cartons, setCartons] = useState([]);
  const [showAddCarton, setShowAddCarton] = useState(false);
  const [currentCarton, setCurrentCarton] = useState({
    productSku: '',
    internalSku: '',
    productName: '',
    cartonCount: 1,
    unitsPerCarton: 1,
    quantity: 1,
    unitOfMeasure: 'each',
    cartonDimensions: { length: '', width: '', height: '', weight: '' },
    itemDimensions: { length: '', width: '', height: '', weight: '' },
    receivingInstructions: cloneReceivingInstructions(),
    serialNumbers: [],
    macAddresses: [],
    declaredValue: '',
    notes: '',
    // Cross-dock fields for cartons
    crossDock: false,
    crossDockShippingArrangement: '', // 'customer_arranges' or 'warehouse_arranges'
    crossDockDestination: {
      useExistingAddress: false,
      selectedAddressId: '',
      newAddress: {
        name: '',
        street_address: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'Canada',
        contact_name: '',
        contact_phone: '',
        notes: ''
      }
    },
    crossDockShippingDetails: {
      carrierPreference: '',
      serviceLevel: '',
      specialInstructions: ''
    },
    crossDockDocuments: {
      providedNow: false,
      bolNumber: '',
      trackingNumber: '',
      documentNotes: ''
    }
  });

  // Modal states
  const [assetModal, setAssetModal] = useState({ show: false, type: '', data: '', target: null });
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null); // Track which item is being edited
  const [currentItem, setCurrentItem] = useState({
    productSku: '',
    internalSku: '',
    productName: '',
    packagingType: 'individual',
    unitOfMeasure: 'each',
    // For cartons
    cartonCount: 1,
    unitsPerCarton: 1,
    // Separate dimensions for cartons and individual items
    cartonDimensions: { length: '', width: '', height: '', weight: '' },
    itemDimensions: { length: '', width: '', height: '', weight: '' },
    receivingInstructions: cloneReceivingInstructions(),
    // For individual items
    quantity: 1,
    // Consolidated dimensions field - holds either carton or individual dimensions based on packagingType
    dimensions: { length: '', width: '', height: '', weight: '' },
    serialNumbers: [],
    macAddresses: [],
    declaredValue: '',
    notes: ''
  });

  // Asset modal enhanced state
  const [, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [assetSaveMessage, setAssetSaveMessage] = useState('');

  // Function to normalize/migrate existing pallets to have all required fields
  const normalizePallet = (pallet) => {
    // Migrate old cartonDimensions/individualDimensions to consolidated dimensions field
    let dimensions = { length: '', width: '', height: '', weight: '' };
    
    if (pallet.dimensions) {
      // Already using new format
      dimensions = pallet.dimensions;
    } else if (pallet.cartonDimensions) {
      // Migrate from old cartonDimensions
      dimensions = pallet.cartonDimensions;
    } else if (pallet.individualDimensions) {
      // Migrate from old individualDimensions
      dimensions = pallet.individualDimensions;
    }
    
    return {
      ...pallet,
      // Ensure new fields exist with defaults
      packagingType: pallet.packagingType || 'individual',
      cartonCount: pallet.cartonCount || 1,
      unitsPerCarton: pallet.unitsPerCarton || 1,
      // Use consolidated dimensions field
      dimensions: dimensions,
      // Ensure existing fields exist
      serialNumbers: pallet.serialNumbers || [],
      macAddresses: pallet.macAddresses || [],
      notes: pallet.notes || ''
    };
  };

  // Initialize local state from formik values (for edit mode or navigation back)
  // Use a ref to track if we're initializing to prevent circular updates
  const isInitializing = useRef(true);
  
  useEffect(() => {
    if (!isInitializing.current) return;
    
    if (formik.values.pallets && Array.isArray(formik.values.pallets) && formik.values.pallets.length > 0) {
      // Normalize pallets to ensure all required fields exist
      const normalizedPallets = formik.values.pallets.map(normalizePallet);
      setPallets(normalizedPallets);
      setShipmentType('pallets');
    }
    
    if (formik.values.cartons && Array.isArray(formik.values.cartons) && formik.values.cartons.length > 0) {
      setCartons(formik.values.cartons);
      if (!formik.values.pallets || formik.values.pallets.length === 0) {
        setShipmentType('cartons');
      }
    }
    
    isInitializing.current = false;
  }, []); // Only run once on mount

  useEffect(() => {
    const sourceAddress = formik.values.sourceAddress;
    if (!sourceAddress || !customerWarehouses.length) return;

    const matchedWarehouse = customerWarehouses.find((warehouse) => {
      const warehouseStreet = warehouse.street_address || warehouse.address_line1 || '';
      const warehouseCity = warehouse.city || '';
      const warehouseState = warehouse.state_province || warehouse.stateProvince || '';
      const warehousePostal = warehouse.postal_code || warehouse.postalCode || '';

      return warehouseStreet === (sourceAddress.street || '')
        && warehouseCity === (sourceAddress.city || '')
        && warehouseState === (sourceAddress.state || sourceAddress.state_province || '')
        && warehousePostal === (sourceAddress.postalCode || sourceAddress.postal_code || '');
    });

    if (matchedWarehouse) {
      setSelectedWarehouse(matchedWarehouse);
      setShowAddAddress(false);
    }
  }, [customerWarehouses, formik.values.sourceAddress]);

  useEffect(() => {
    const destinationWarehouseId = formik.values.destinationWarehouseId;
    if (!destinationWarehouseId || !accessibleWarehouses.length) return;

    const matchedWarehouse = accessibleWarehouses.find((warehouse) => warehouse.id === destinationWarehouseId);
    if (matchedWarehouse) {
      setSelectedDestinationWarehouse(matchedWarehouse);
      if (setSelectedDestinationWarehouseName) {
        setSelectedDestinationWarehouseName(matchedWarehouse.name);
      }
    }
  }, [accessibleWarehouses, formik.values.destinationWarehouseId, setSelectedDestinationWarehouseName]);

  useEffect(() => {
    if (formik.values.destinationWarehouseId || accessibleWarehouses.length !== 1) return;

    const [onlyWarehouse] = accessibleWarehouses;
    if (!onlyWarehouse?.id) return;

    handleDestinationWarehouseSelect(onlyWarehouse);
  }, [accessibleWarehouses, formik.values.destinationWarehouseId]);

  // Sync local state to formik - only when local arrays actually change, not during initialization  
  useEffect(() => {
    if (isInitializing.current) return;
    formik.setFieldValue('pallets', pallets, false);
  }, [pallets]);

  useEffect(() => {
    if (isInitializing.current) return;
    formik.setFieldValue('cartons', cartons, false);
  }, [cartons]);



  // Extract the primitive companyId value to avoid re-triggering this effect
  // when Formik rebuilds the values object on unrelated re-renders.
  const companyId = formik.values.companyId;

  // Fetch customer warehouses/shipping addresses
  useEffect(() => {
    if (isAdmin && companyId) {
      // Admin user with company selected
      fetchCustomerWarehouses();
      fetchAccessibleWarehouses();
      // Reset search results when company changes
      setSearchResults([]);
      setSearchTerm('');
      setSelectedProduct(null);
      setShowProductDropdown(false);
    } else if (!isAdmin) {
      // Customer user - always fetch their warehouses
      fetchCustomerWarehouses();
      fetchAccessibleWarehouses();
    }
  }, [companyId, isAdmin]);

  const fetchCustomerWarehouses = async () => {
    try {
      if (isAdmin && formik.values.companyId) {
        // Admin user - fetch customer's warehouses
        const response = await api.get(`/admin/customers/${formik.values.companyId}/warehouse-access`);
        setCustomerWarehouses(response.data?.companyWarehouses || []);
      } else if (!isAdmin) {
        // Customer user - fetch their own warehouses
        const response = await api.get('/customers/warehouses');
        const warehouseData = response.data?.data || [];
        setCustomerWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      }
    } catch (error) {
      console.error('Error fetching customer warehouses:', error);
      setCustomerWarehouses([]);
    }
  };

  const fetchAccessibleWarehouses = async () => {
    try {
      if (isAdmin && formik.values.companyId) {
        // Admin user - fetch customer's accessible warehouses
        const response = await api.get(`/admin/customers/${formik.values.companyId}/accessible-warehouses`);
        setAccessibleWarehouses((response.data?.accessibleWarehouses || []).map(normalizeWarehouseAddress));
      } else if (!isAdmin) {
        // Customer user - fetch accessible internal warehouses (DESTINATION warehouses)
        const response = await api.get('/customers/accessible-warehouses');
        const warehouseData = response.data?.data || [];
        setAccessibleWarehouses(Array.isArray(warehouseData) ? warehouseData.map(normalizeWarehouseAddress) : []);
      }
    } catch (error) {
      console.error('Error fetching accessible warehouses:', error);
      setAccessibleWarehouses([]);
    }
  };

  // Handle date change with validation
  const handleDateChange = (date) => {
    // Only allow today or future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date && date < today) {
      alert('Scheduled date cannot be in the past');
      return;
    }
    
    formik.setFieldValue('scheduledDate', date);
  };

  // Handle warehouse selection
  const handleWarehouseSelect = (warehouse) => {
    setSelectedWarehouse(warehouse);
    formik.setFieldValue('sourceAddressId', warehouse.id);
    formik.setFieldValue('sourceAddress', {
      street: warehouse.street_address,
      addressLine2: warehouse.address_line_2 || '',
      city: warehouse.city,
      state: warehouse.state_province,
      postalCode: warehouse.postal_code,
      country: warehouse.country
    });
    setShowAddAddress(false);
  };

  // Handle destination warehouse selection
  const handleDestinationWarehouseSelect = (warehouse) => {
    const normalizedWarehouse = normalizeWarehouseAddress(warehouse);
    setSelectedDestinationWarehouse(normalizedWarehouse);
    formik.setFieldValue('destinationWarehouseId', normalizedWarehouse.id);
    if (setSelectedDestinationWarehouseName) {
      setSelectedDestinationWarehouseName(normalizedWarehouse.name);
    }
  };

  // Handle new address form changes
  const handleNewAddressChange = (field, value) => {
    setNewAddressForm(prev => ({ ...prev, [field]: value }));
    
    // Handle phone number formatting and validation
    if (field === 'contact_phone') {
      const formatted = formatPhoneNumber(value);
      setNewAddressForm(prev => ({ ...prev, contact_phone: formatted }));
      
      const validation = validatePhoneNumber(formatted);
      setPhoneError(validation.isValid ? '' : validation.error);
    }
    
    // Handle postal code validation
    if (field === 'postal_code') {
      const validation = validatePostalCode(value, newAddressForm.country);
      setPostalCodeError(validation.isValid ? '' : validation.error);
    }
  };

  // Reset new address form
  const resetNewAddressForm = () => {
    setNewAddressForm({
      name: '',
      street_address: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: 'Canada',
      contact_name: '',
      contact_phone: '',
      notes: ''
    });
    setPhoneError('');
    setPostalCodeError('');
  };

  // Handle adding new source address
  const handleSaveNewAddress = async () => {
    // Validate required fields
    if (!newAddressForm.name.trim()) {
      toast.error('Please enter a warehouse name');
      return;
    }

    if (!newAddressForm.street_address.trim() || !newAddressForm.city.trim()) {
      toast.error('Please fill in at least warehouse name, street address and city');
      return;
    }

    // Check for validation errors
    if (phoneError || postalCodeError) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    try {
      const addressData = {
        ...newAddressForm,
        is_active: true
      };

      const endpoint = isAdmin
        ? `/admin/customers/${formik.values.companyId}/company-warehouses`
        : `/customers/warehouses`;

      const response = await api.post(endpoint, addressData);
      await fetchCustomerWarehouses(); // Refresh the list
      setShowAddAddress(false);
      resetNewAddressForm();
      toast.success('Address saved successfully');

      // Auto-select the newly created address
      const newAddress = response.data?.data || response.data;
      if (newAddress?.id) {
        handleWarehouseSelect(newAddress);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('Failed to save address');
    }
  };
  
  // Get available states based on selected country
  const availableStates = getStatesForCountry(newAddressForm.country);

  // Handle asset modal (serial numbers / MAC addresses)
  const getTargetAssetValues = useCallback((target, type) => {
    if (target === 'currentPallet') {
      return currentPallet[type] || [];
    }
    if (target === 'currentCarton') {
      return currentCarton[type] || [];
    }
    if (target === 'currentItem') {
      return currentItem[type] || [];
    }
    return [];
  }, [currentPallet, currentCarton, currentItem]);

  const updateTargetAssetValues = (target, type, assets) => {
    if (target === 'currentPallet') {
      setCurrentPallet(prev => ({
        ...prev,
        [type]: assets
      }));
    } else if (target === 'currentCarton') {
      setCurrentCarton(prev => ({
        ...prev,
        [type]: assets
      }));
    } else if (target === 'currentItem') {
      setCurrentItem(prev => ({
        ...prev,
        [type]: assets
      }));
    }
  };

  const openAssetModal = (target, type) => {
    setAssetModal({ show: true, type, data: '', target });
    setValidationErrors([]);
    setAssetSaveMessage('');
    setCsvFile(null);
    setCsvData('');
  };

  const applyAssetEntries = async (assetString, { overwrite = false } = {}) => {
    const currentSku = getCurrentProductSku();
    const currentQuantity = getCurrentProductQuantity();
    const existingAssets = getTargetAssetValues(assetModal.target, assetModal.type);

    const validation = validateAssets(assetString, assetModal.type);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return false;
    }

    const inputAssets = validation.assets;

    if (inputAssets.length === 0) {
      setValidationErrors([]);
      setAssetSaveMessage('');
      return true;
    }

    const mergedAssets = overwrite
      ? inputAssets
      : [...existingAssets, ...inputAssets];

    const mergedDuplicates = mergedAssets.filter((asset, index) => mergedAssets.indexOf(asset) !== index);
    if (mergedDuplicates.length > 0) {
      setValidationErrors([`Duplicate entries found: ${[...new Set(mergedDuplicates)].join(', ')}`]);
      return false;
    }

    if (assetModal.type === 'serialNumbers' && mergedAssets.length > 0 && currentQuantity > 0) {
      if (mergedAssets.length > currentQuantity) {
        setValidationErrors([
          `Serial number count (${mergedAssets.length}) cannot exceed product quantity (${currentQuantity})`
        ]);
        return false;
      }
    }

    if (assetModal.type === 'serialNumbers' && mergedAssets.length > 0) {
      const duplicates = await checkDuplicateSerials(mergedAssets, currentSku);
      if (duplicates.length > 0) {
        setValidationErrors([`The following serial numbers already exist in the database: ${duplicates.join(', ')}`]);
        return false;
      }
    }

    updateTargetAssetValues(assetModal.target, assetModal.type, mergedAssets);
    setAssetModal(prev => ({ ...prev, data: '' }));
    setValidationErrors([]);
    setAssetSaveMessage(
      overwrite
        ? `${formatAssetTypeLabel(assetModal.type)} replaced (${mergedAssets.length})`
        : `${inputAssets.length} ${assetModal.type === 'serialNumbers' ? 'serial number' : 'MAC address'}${inputAssets.length === 1 ? '' : 'es'} added`
    );
    setCsvFile(null);
    setCsvData('');
    return true;
  };

  const handleAssetModalSubmit = async () => {
    const success = await applyAssetEntries(assetModal.data, { overwrite: false });
    if (!success) return;

    setAssetModal({ show: false, type: '', data: '', target: null });
    setValidationErrors([]);
    setAssetSaveMessage('');
    setCsvFile(null);
    setCsvData('');
  };

  const handleCsvReplace = async () => {
    const existingAssets = getTargetAssetValues(assetModal.target, assetModal.type);

    if (!csvData) return;

    if (existingAssets.length > 0) {
      const confirmed = window.confirm(
        `Replace the existing ${formatAssetTypeLabel(assetModal.type).toLowerCase()} with the CSV upload? This will overwrite the current list.`
      );
      if (!confirmed) return;
    }

    await applyAssetEntries(csvData, { overwrite: true });
  };

  const removeAssetValue = (valueToRemove) => {
    const existingAssets = getTargetAssetValues(assetModal.target, assetModal.type);
    const updatedAssets = existingAssets.filter((value) => value !== valueToRemove);
    updateTargetAssetValues(assetModal.target, assetModal.type, updatedAssets);
    setAssetSaveMessage('');
    setValidationErrors([]);
  };

  const clearAssetValues = () => {
    updateTargetAssetValues(assetModal.target, assetModal.type, []);
    setAssetSaveMessage('');
    setValidationErrors([]);
  };



  // Product search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchProducts = async () => {
    if (!formik.values.companyId) {
      return;
    }
    
    try {
      // Use the customer inventory search endpoint to get existing SKUs for this customer
      const response = await api.get('/inventory/search', {
        params: { query: '' }, // Empty query to get all items for this customer
        headers: { 'Customer-ID': formik.values.companyId }
      });
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error fetching customer inventory:', error);
      setSearchResults([]);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCurrentItem(prev => ({ ...prev, productSku: term }));
    
    if (term.length > 1 && searchResults.length > 0) {
      const filteredResults = searchResults.filter(item =>
        item.productSku.toLowerCase().includes(term.toLowerCase()) ||
        item.productName.toLowerCase().includes(term.toLowerCase()) ||
        (item.internalSku && item.internalSku.toLowerCase().includes(term.toLowerCase()))
      );
      setShowProductDropdown(filteredResults.length > 0);
    } else if (term.length > 1) {
      // If no local results, search the API
      searchCustomerInventory(term);
    } else {
      setShowProductDropdown(false);
    }
  };

  const searchCustomerInventory = async (query) => {
    if (!formik.values.companyId) return;
    
    try {
      const response = await api.get('/inventory/search', {
        params: { query },
        headers: { 'Customer-ID': formik.values.companyId }
      });
      const results = response.data || [];
      setSearchResults(results);
      setShowProductDropdown(results.length > 0);
    } catch (error) {
      console.error('Error searching customer inventory:', error);
      setSearchResults([]);
      setShowProductDropdown(false);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm(product.productSku);
    setCurrentItem(prev => ({
      ...prev,
      productSku: product.productSku,
      internalSku: product.internalSku || prev.internalSku,
      productName: product.productName,
      // Prefill dimensions and weights if available
      cartonDimensions: product.cartonDimensions || prev.cartonDimensions,
      individualDimensions: product.individualDimensions || prev.individualDimensions,
      unitOfMeasure: product.unitOfMeasurement || prev.unitOfMeasure,
      // Also set weight from the product data
      weight: product.weight || prev.weight || ''
    }));
    setShowProductDropdown(false);
  };

  const handleProductCancel = () => {
    setSelectedProduct(null);
    setShowProductDropdown(false);
  };

  // Check for duplicate SKUs
  const isDuplicateSku = (sku) => {
    if (!sku) return false;
    
    // Check if SKU exists in current pallet items
    if (currentPallet.items && currentPallet.items.some(item => item.productSku === sku)) {
      return true;
    }
    
    // Check if SKU exists in other pallets
    if (pallets.some(pallet => 
      pallet.productSku === sku || 
      (pallet.items && pallet.items.some(item => item.productSku === sku))
    )) {
      return true;
    }
    
    // Check if SKU exists in cartons
    if (cartons.some(carton => carton.productSku === sku)) {
      return true;
    }
    
    return false;
  };

  // Validate serial numbers/MAC addresses
  const validateAssets = (assetString, assetType) => {
    const errors = [];
    const assets = splitAssetValues(assetString);
    
    // Check for spaces
    assets.forEach((asset, index) => {
      if (asset.includes(' ')) {
        errors.push(`Item ${index + 1}: "${asset}" contains spaces`);
      }
    });
    
    // Check for duplicates within the list
    const duplicates = assets.filter((asset, index) => assets.indexOf(asset) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate entries found: ${[...new Set(duplicates)].join(', ')}`);
    }
    
    // Check length limits
    if (assets.length > 1000) {
      errors.push(`Too many entries: ${assets.length}/1000 maximum`);
    }
    
    // Validate format for MAC addresses
    if (assetType === 'macAddresses') {
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
      assets.forEach((mac, index) => {
        if (!macRegex.test(mac)) {
          errors.push(`Item ${index + 1}: "${mac}" is not a valid MAC address format`);
        }
      });
    }
    
    return { isValid: errors.length === 0, errors, assets };
  };

  // Check for duplicate serial numbers in database
  const checkDuplicateSerials = async (serialNumbers, sku) => {
    if (!serialNumbers.length || !sku || !formik.values.companyId) return [];
    
    try {
      const response = await api.post('/inventory/check-duplicate-serials', {
        serials: serialNumbers,
        companyId: formik.values.companyId
      });
      return response.data.duplicates || [];
    } catch (error) {
      console.error('Error checking duplicate serials:', error);
      return [];
    }
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      // Join as comma-separated for consistency with manual entry
      const csvAsString = lines.join(',');
      setCsvData(csvAsString);
    };
    reader.readAsText(file);
  };

  // Get current product quantity for validation
  const getCurrentProductQuantity = () => {
    const { target } = assetModal;
    
    if (target === 'currentPallet') {
      // For single-SKU pallets, check packaging type
      if (currentPallet.packagingType === 'carton') {
        return currentPallet.cartonCount * currentPallet.unitsPerCarton;
      } else {
        return currentPallet.quantity || 0;
      }
    } else if (target === 'currentCarton') {
      return currentCarton.quantity;
    } else if (target === 'currentItem') {
      return currentItem.packagingType === 'carton' 
        ? currentItem.cartonCount * currentItem.unitsPerCarton 
        : currentItem.quantity;
    }
    
    return 0;
  };

  // Get current product SKU for validation
  const getCurrentProductSku = () => {
    const { target } = assetModal;
    
    if (target === 'currentPallet') {
      return currentPallet.productSku;
    } else if (target === 'currentCarton') {
      return currentCarton.productSku;
    } else if (target === 'currentItem') {
      return currentItem.productSku;
    }
    
    return '';
  };

  // Helper function to calculate asset status for pallets/cartons
  const getAssetStatus = (item, type = 'pallet') => {
    if (type === 'pallet') {
      if (item.type === 'single') {
        // Pallet-only mode removed - all pallets now require product details
        
        const expectedQty = item.packagingType === 'carton' 
          ? item.cartonCount * item.unitsPerCarton 
          : item.quantity;
        const serialCount = (item.serialNumbers || []).length;
        const macCount = (item.macAddresses || []).length;
        
        return {
          expectedQty,
          serialCount,
          macCount,
          serialStatus: serialCount === expectedQty ? 'complete' : serialCount > 0 ? 'partial' : 'missing',
          macStatus: macCount === expectedQty ? 'complete' : macCount > 0 ? 'partial' : 'missing'
        };
      } else {
        // Multi-SKU pallet - aggregate from items
        let totalExpected = 0;
        let totalSerials = 0;
        let totalMacs = 0;
        
        (item.items || []).forEach(subItem => {
          const itemQty = subItem.packagingType === 'carton' 
            ? subItem.cartonCount * subItem.unitsPerCarton 
            : subItem.quantity;
          totalExpected += itemQty;
          totalSerials += (subItem.serialNumbers || []).length;
          totalMacs += (subItem.macAddresses || []).length;
        });
        
        return {
          expectedQty: totalExpected,
          serialCount: totalSerials,
          macCount: totalMacs,
          serialStatus: totalSerials === totalExpected ? 'complete' : totalSerials > 0 ? 'partial' : 'missing',
          macStatus: totalMacs === totalExpected ? 'complete' : totalMacs > 0 ? 'partial' : 'missing'
        };
      }
    } else if (type === 'carton') {
      const expectedQty = item.cartonCount * item.unitsPerCarton;
      const serialCount = (item.serialNumbers || []).length;
      const macCount = (item.macAddresses || []).length;
      
      return {
        expectedQty,
        serialCount,
        macCount,
        serialStatus: serialCount === expectedQty ? 'complete' : serialCount > 0 ? 'partial' : 'missing',
        macStatus: macCount === expectedQty ? 'complete' : macCount > 0 ? 'partial' : 'missing'
      };
    } else if (type === 'item') {
      // Individual item within a multi-SKU pallet
      const expectedQty = item.packagingType === 'carton' 
        ? item.cartonCount * item.unitsPerCarton 
        : item.quantity;
      const serialCount = (item.serialNumbers || []).length;
      const macCount = (item.macAddresses || []).length;
      
      return {
        expectedQty,
        serialCount,
        macCount,
        serialStatus: serialCount === expectedQty ? 'complete' : serialCount > 0 ? 'partial' : 'missing',
        macStatus: macCount === expectedQty ? 'complete' : macCount > 0 ? 'partial' : 'missing'
      };
    }
    
    return { expectedQty: 0, serialCount: 0, macCount: 0, serialStatus: 'missing', macStatus: 'missing' };
  };

  // Helper function to get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'complete':
        return { color: 'text-green-600', icon: LuCheck };
      case 'partial':
        return { color: 'text-yellow-600', icon: LuTriangleAlert };
      case 'missing':
        return { color: 'text-red-600', icon: LuX };
      default:
        return { color: 'text-gray-400', icon: null };
    }
  };

  // Validation function to check for duplicate assets against existing inventory
  const validateAgainstExistingInventory = useCallback(async (assets, sku, companyId) => {
    try {
      const response = await api.post('/inventory/validate-assets', {
        assets,
        sku,
        companyId
      });
      
      return response.data; // Should return { valid: boolean, duplicates: [] }
    } catch (error) {
      console.error('Error validating assets against inventory:', error);
      console.error('Error response:', error.response?.data);
      return { valid: false, duplicates: [], error: 'Validation failed' };
    }
  }, []);

  // Function to collect all assets from current ASN for cross-validation
  const collectAllAsnAssets = useCallback(() => {
    const allAssets = {
      serialNumbers: [],
      macAddresses: []
    };

    // Collect from pallets
    pallets.forEach(pallet => {
      if (pallet.type === 'single') {
        // Single SKU pallet
        if (pallet.serialNumbers) {
          allAssets.serialNumbers.push(...pallet.serialNumbers.map(sn => ({
            value: sn,
            sku: pallet.productSku,
            source: `Pallet: ${pallet.label}`
          })));
        }
        if (pallet.macAddresses) {
          allAssets.macAddresses.push(...pallet.macAddresses.map(mac => ({
            value: mac,
            sku: pallet.productSku,
            source: `Pallet: ${pallet.label}`
          })));
        }
      } else if (pallet.type === 'multi') {
        // Multi-SKU pallet
        (pallet.items || []).forEach(item => {
          if (item.serialNumbers) {
            allAssets.serialNumbers.push(...item.serialNumbers.map(sn => ({
              value: sn,
              sku: item.productSku,
              source: `Pallet: ${pallet.label} - Item: ${item.productName}`
            })));
          }
          if (item.macAddresses) {
            allAssets.macAddresses.push(...item.macAddresses.map(mac => ({
              value: mac,
              sku: item.productSku,
              source: `Pallet: ${pallet.label} - Item: ${item.productName}`
            })));
          }
        });
      }
    });

    // Collect from cartons
    cartons.forEach(carton => {
      if (carton.serialNumbers) {
        allAssets.serialNumbers.push(...carton.serialNumbers.map(sn => ({
          value: sn,
          sku: carton.productSku,
          source: `Carton: ${carton.productName}`
        })));
      }
      if (carton.macAddresses) {
        allAssets.macAddresses.push(...carton.macAddresses.map(mac => ({
          value: mac,
          sku: carton.productSku,
          source: `Carton: ${carton.productName}`
        })));
      }
    });

    return allAssets;
  }, [pallets, cartons]);

  // Function to find duplicates within the current ASN
  const findAsnInternalDuplicates = useCallback(() => {
    const allAssets = collectAllAsnAssets();
    const duplicates = [];

    // Check serial number duplicates
    const serialCounts = {};
    allAssets.serialNumbers.forEach(item => {
      const key = `${item.sku}-${item.value}`;
      if (!serialCounts[key]) {
        serialCounts[key] = [];
      }
      serialCounts[key].push(item);
    });

    Object.entries(serialCounts).forEach(([key, items]) => {
      if (items.length > 1) {
        duplicates.push({
          type: 'serial',
          value: items[0].value,
          sku: items[0].sku,
          sources: items.map(item => item.source)
        });
      }
    });

    // Check MAC address duplicates
    const macCounts = {};
    allAssets.macAddresses.forEach(item => {
      const key = `${item.sku}-${item.value}`;
      if (!macCounts[key]) {
        macCounts[key] = [];
      }
      macCounts[key].push(item);
    });

    Object.entries(macCounts).forEach(([key, items]) => {
      if (items.length > 1) {
        duplicates.push({
          type: 'mac',
          value: items[0].value,
          sku: items[0].sku,
          sources: items.map(item => item.source)
        });
      }
    });

    return duplicates;
  }, [pallets, cartons]);

  // Comprehensive validation function for the entire ASN
  const validateEntireAsn = useCallback(async () => {
    try {
      const errors = [];
      const warnings = [];

      // 1. Check for internal duplicates within the ASN
      const internalDuplicates = findAsnInternalDuplicates();
      if (internalDuplicates.length > 0) {
        internalDuplicates.forEach(dup => {
          errors.push({
            type: 'internal_duplicate',
            message: `Duplicate ${dup.type === 'serial' ? 'Serial Number' : 'MAC Address'} "${dup.value}" for SKU ${dup.sku} found in: ${dup.sources.join(', ')}`
          });
        });
      }

      // 2. Check against existing inventory for each unique SKU
      const uniqueSkus = new Set();
      const allAssets = collectAllAsnAssets();
      
      // Collect unique SKUs
      [...allAssets.serialNumbers, ...allAssets.macAddresses].forEach(item => {
        uniqueSkus.add(item.sku);
      });

      // Check if companyId is available
      if (!formik.values.companyId) {
        return { errors: [], warnings: [{ type: 'no_company', message: 'Please select a company before validating' }], isValid: true };
      }

      // Validate each SKU against existing inventory
      for (const sku of uniqueSkus) {
        const skuSerials = allAssets.serialNumbers.filter(item => item.sku === sku).map(item => item.value);
        const skuMacs = allAssets.macAddresses.filter(item => item.sku === sku).map(item => item.value);
        
        if (skuSerials.length > 0 || skuMacs.length > 0) {
          try {
            const validation = await validateAgainstExistingInventory(
              {
                serialNumbers: skuSerials,
                macAddresses: skuMacs
              },
              sku,
              formik.values.companyId
            );

            if (!validation.valid && validation.duplicates && validation.duplicates.length > 0) {
              validation.duplicates.forEach(duplicate => {
                errors.push({
                  type: 'existing_inventory_duplicate',
                  message: `${duplicate.type === 'serial' ? 'Serial Number' : 'MAC Address'} "${duplicate.value}" for SKU ${sku} already exists in your inventory`
                });
              });
            }
          } catch (error) {
            console.error(`Error validating SKU ${sku}:`, error);
            console.error('API validation failed, adding warning instead of error');
            warnings.push({
              type: 'validation_error',
              message: `Could not validate assets for SKU ${sku} against existing inventory. Please verify manually.`
            });
          }
        }
      }

      const result = { errors, warnings, isValid: errors.length === 0 };
      return result;
    } catch (error) {
      console.error('Error in validateEntireAsn:', error);
      throw error;
    }
  }, [pallets, cartons, formik.values.companyId, collectAllAsnAssets, findAsnInternalDuplicates, validateAgainstExistingInventory]);

  // Expose validation function to parent component
  useEffect(() => {
    if (onValidationRequest) {
      onValidationRequest(validateEntireAsn);
    }
  }, [onValidationRequest, validateEntireAsn]);

  // Add crossdock helper functions
  const handleCrossDockToggle = (type, index, enabled) => {
    if (type === 'pallet') {
      const updatedPallets = [...pallets];
      updatedPallets[index] = {
        ...updatedPallets[index],
        crossDock: enabled,
        // Reset crossdock fields when disabled
        crossDockShippingArrangement: enabled ? updatedPallets[index].crossDockShippingArrangement : '',
        crossDockDestination: enabled ? updatedPallets[index].crossDockDestination : {
          useExistingAddress: false,
          selectedAddressId: '',
          newAddress: {
            name: '',
            street_address: '',
            address_line_2: '',
            city: '',
            state_province: '',
            postal_code: '',
            country: 'Canada',
            contact_name: '',
            contact_phone: '',
            notes: ''
          }
        },
        crossDockShippingDetails: enabled ? updatedPallets[index].crossDockShippingDetails : {
          carrierPreference: '',
          serviceLevel: '',
          specialInstructions: ''
        },
        crossDockDocuments: enabled ? updatedPallets[index].crossDockDocuments : {
          providedNow: false,
          bolNumber: '',
          trackingNumber: '',
          documentNotes: ''
        }
      };
      setPallets(updatedPallets);
    } else if (type === 'carton') {
      const updatedCartons = [...cartons];
      updatedCartons[index] = {
        ...updatedCartons[index],
        crossDock: enabled,
        // Reset crossdock fields when disabled
        crossDockShippingArrangement: enabled ? updatedCartons[index].crossDockShippingArrangement : '',
        crossDockDestination: enabled ? updatedCartons[index].crossDockDestination : {
          useExistingAddress: false,
          selectedAddressId: '',
          newAddress: {
            name: '',
            street_address: '',
            address_line_2: '',
            city: '',
            state_province: '',
            postal_code: '',
            country: 'Canada',
            contact_name: '',
            contact_phone: '',
            notes: ''
          }
        },
        crossDockShippingDetails: enabled ? updatedCartons[index].crossDockShippingDetails : {
          carrierPreference: '',
          serviceLevel: '',
          specialInstructions: ''
        },
        crossDockDocuments: enabled ? updatedCartons[index].crossDockDocuments : {
          providedNow: false,
          bolNumber: '',
          trackingNumber: '',
          documentNotes: ''
        }
      };
      setCartons(updatedCartons);
    }
  };

  const handleCrossDockShippingArrangementChange = (type, index, arrangement) => {
    if (type === 'pallet') {
      const updatedPallets = [...pallets];
      updatedPallets[index] = {
        ...updatedPallets[index],
        crossDockShippingArrangement: arrangement
      };
      setPallets(updatedPallets);
    } else if (type === 'carton') {
      const updatedCartons = [...cartons];
      updatedCartons[index] = {
        ...updatedCartons[index],
        crossDockShippingArrangement: arrangement
      };
      setCartons(updatedCartons);
    }
  };

  const handleCrossDockDestinationChange = (type, index, field, value) => {
    if (type === 'pallet') {
      const updatedPallets = [...pallets];
      if (field.startsWith('newAddress.')) {
        const addressField = field.replace('newAddress.', '');
        updatedPallets[index] = {
          ...updatedPallets[index],
          crossDockDestination: {
            ...updatedPallets[index].crossDockDestination,
            newAddress: {
              ...updatedPallets[index].crossDockDestination.newAddress,
              [addressField]: value
            }
          }
        };
      } else {
        updatedPallets[index] = {
          ...updatedPallets[index],
          crossDockDestination: {
            ...updatedPallets[index].crossDockDestination,
            [field]: value
          }
        };
      }
      setPallets(updatedPallets);
    } else if (type === 'carton') {
      const updatedCartons = [...cartons];
      if (field.startsWith('newAddress.')) {
        const addressField = field.replace('newAddress.', '');
        updatedCartons[index] = {
          ...updatedCartons[index],
          crossDockDestination: {
            ...updatedCartons[index].crossDockDestination,
            newAddress: {
              ...updatedCartons[index].crossDockDestination.newAddress,
              [addressField]: value
            }
          }
        };
      } else {
        updatedCartons[index] = {
          ...updatedCartons[index],
          crossDockDestination: {
            ...updatedCartons[index].crossDockDestination,
            [field]: value
          }
        };
      }
      setCartons(updatedCartons);
    }
  };

  const handleCrossDockShippingDetailsChange = (type, index, field, value) => {
    if (type === 'pallet') {
      const updatedPallets = [...pallets];
      updatedPallets[index] = {
        ...updatedPallets[index],
        crossDockShippingDetails: {
          ...updatedPallets[index].crossDockShippingDetails,
          [field]: value
        }
      };
      setPallets(updatedPallets);
    } else if (type === 'carton') {
      const updatedCartons = [...cartons];
      updatedCartons[index] = {
        ...updatedCartons[index],
        crossDockShippingDetails: {
          ...updatedCartons[index].crossDockShippingDetails,
          [field]: value
        }
      };
      setCartons(updatedCartons);
    }
  };

  const handleCrossDockDocumentsChange = (type, index, field, value) => {
    if (type === 'pallet') {
      const updatedPallets = [...pallets];
      updatedPallets[index] = {
        ...updatedPallets[index],
        crossDockDocuments: {
          ...updatedPallets[index].crossDockDocuments,
          [field]: value
        }
      };
      setPallets(updatedPallets);
    } else if (type === 'carton') {
      const updatedCartons = [...cartons];
      updatedCartons[index] = {
        ...updatedCartons[index],
        crossDockDocuments: {
          ...updatedCartons[index].crossDockDocuments,
          [field]: value
        }
      };
      setCartons(updatedCartons);
    }
  };

  return (
    <div className="space-y-6">
      {/* Scheduled Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Scheduled Date *</label>
        <DatePicker
          selected={normalizeDateValue(formik.values.scheduledDate)}
          onChange={handleDateChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholderText="Select date"
          dateFormat="yyyy-MM-dd"
          minDate={new Date()}
          filterDate={(date) => {
            // Only allow today and future dates
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date >= today;
          }}
        />
        {formik.errors.scheduledDate && formik.touched.scheduledDate && (
          <p className="mt-1 text-sm text-red-600">{formik.errors.scheduledDate}</p>
        )}
      </div>

      {/* Reference Number and Vendor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Reference Number</label>
          <input
            type="text"
            placeholder="PO Number, Invoice #, etc."
            value={formik.values.reference_number || ''}
            onChange={(e) => formik.setFieldValue('reference_number', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            maxLength={100}
          />
          {formik.errors.reference_number && formik.touched.reference_number && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.reference_number}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Optional: Purchase order, invoice number, or other reference</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Vendor</label>
          <input
            type="text"
            placeholder="Vendor or supplier name"
            value={formik.values.vendor || ''}
            onChange={(e) => formik.setFieldValue('vendor', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            maxLength={255}
          />
          {formik.errors.vendor && formik.touched.vendor && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.vendor}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Optional: Name of the vendor or supplier</p>
        </div>
      </div>

      {/* Source Address Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Source Address *</label>
        
        {/* Warehouse Selection */}
        <div className="mb-4">
          <select
            value={selectedWarehouse?.id || ''}
            onChange={(e) => {
              if (e.target.value === 'new') {
                setShowAddAddress(true);
                setSelectedWarehouse(null);
              } else if (e.target.value) {
                const warehouse = customerWarehouses.find(w => w.id === e.target.value);
                handleWarehouseSelect(warehouse);
              } else {
                setSelectedWarehouse(null);
                setShowAddAddress(false); // Don't show form when clearing selection
              }
            }}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Select a saved address or add a new one</option>
            {customerWarehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} - {warehouse.street_address}, {warehouse.city}
              </option>
            ))}
            <option value="new">+ Add new address</option>
          </select>
        </div>

        {selectedWarehouse && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Source: {selectedWarehouse.name}
                </p>
                <p className="text-xs text-green-700">
                  {selectedWarehouse.street_address}, {selectedWarehouse.city}, {selectedWarehouse.state_province}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Manual Address Entry - Only show when specifically requested */}
        {showAddAddress && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Enter New Source Address</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Warehouse Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Warehouse Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Main Warehouse, Distribution Center"
                  value={newAddressForm.name}
                  onChange={(e) => handleNewAddressChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Street Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Street Address *
                </label>
                <input
                  type="text"
                  placeholder="Street address"
                  value={newAddressForm.street_address}
                  onChange={(e) => handleNewAddressChange('street_address', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Address Line 2 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Address Line 2
                </label>
                <input
                  type="text"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  value={newAddressForm.address_line_2}
                  onChange={(e) => handleNewAddressChange('address_line_2', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City *
                </label>
                <input
                  type="text"
                  placeholder="City"
                  value={newAddressForm.city}
                  onChange={(e) => handleNewAddressChange('city', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* State/Province */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {newAddressForm.country === 'Canada' ? 'Province' : 'State'} *
                </label>
                <select
                  value={newAddressForm.state_province}
                  onChange={(e) => handleNewAddressChange('state_province', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Select {newAddressForm.country === 'Canada' ? 'Province' : 'State'}</option>
                  {availableStates.map(state => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {newAddressForm.country === 'Canada' ? 'Postal Code' : 'ZIP Code'}
                </label>
                <input
                  type="text"
                  placeholder={newAddressForm.country === 'Canada' ? 'A1A 1A1' : '12345'}
                  value={newAddressForm.postal_code}
                  onChange={(e) => handleNewAddressChange('postal_code', e.target.value)}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    postalCodeError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {postalCodeError && (
                  <p className="mt-1 text-sm text-red-600">{postalCodeError}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Country *
                </label>
                <select
                  value={newAddressForm.country}
                  onChange={(e) => handleNewAddressChange('country', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  {COUNTRIES.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contact Name
                </label>
                <input
                  type="text"
                  placeholder="Contact person name"
                  value={newAddressForm.contact_name}
                  onChange={(e) => handleNewAddressChange('contact_name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contact Phone
                </label>
                <input
                  type="text"
                  placeholder="+1-xxx-xxx-xxxx"
                  value={newAddressForm.contact_phone}
                  onChange={(e) => handleNewAddressChange('contact_phone', e.target.value)}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    phoneError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                )}
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  rows="3"
                  placeholder="Additional notes about this address"
                  value={newAddressForm.notes}
                  onChange={(e) => handleNewAddressChange('notes', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddAddress(false);
                  resetNewAddressForm();
                }}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNewAddress}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Save Address
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Destination Warehouse Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Destination Warehouse *</label>
        
        <select
          value={selectedDestinationWarehouse?.id || ''}
          onChange={(e) => {
            if (e.target.value) {
              const warehouse = accessibleWarehouses.find(w => w.id === e.target.value);
              handleDestinationWarehouseSelect(warehouse);
            } else {
              setSelectedDestinationWarehouse(null);
              formik.setFieldValue('destinationWarehouseId', '');
            }
          }}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">Select destination warehouse</option>
          {accessibleWarehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </option>
          ))}
        </select>
        
        {selectedDestinationWarehouse && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Destination: {selectedDestinationWarehouse.name}
                </p>
                <p className="text-xs text-green-700">
                  {formatWarehouseAddress(selectedDestinationWarehouse)}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {formik.errors.destinationWarehouseId && formik.touched.destinationWarehouseId && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.destinationWarehouseId}</p>
        )}
      </div>

      {/* Shipment Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">What are you sending? *</label>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              shipmentType === 'pallets' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => {
              setShipmentType('pallets');
              // Don't clear cartons - user might want to switch back
            }}
          >
            <div className="flex items-center">
              <FiBox className="h-8 w-8 text-blue-500 mr-4" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Pallets {pallets.length > 0 && <span className="text-sm text-indigo-600">({pallets.length})</span>}
                </h3>
                <p className="text-sm text-gray-500">Items organized on pallets</p>
                <p className="text-xs text-gray-400 mt-1">Bulk shipments, warehouse storage</p>
              </div>
            </div>
          </div>
          
          <div
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              shipmentType === 'cartons' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => {
              setShipmentType('cartons');
              // Don't clear pallets - user might want to switch back
            }}
          >
            <div className="flex items-center">
              <FiPackage className="h-8 w-8 text-green-500 mr-4" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Cartons {cartons.length > 0 && <span className="text-sm text-indigo-600">({cartons.length})</span>}
                </h3>
                <p className="text-sm text-gray-500">Individual cartons/boxes</p>
                <p className="text-xs text-gray-400 mt-1">Smaller shipments, individual items</p>
              </div>
            </div>
          </div>
        </div>
        
        {!shipmentType && (
          <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
            Please select what type of shipment you're sending to continue
          </p>
        )}
      </div>

      {/* Pallet Management Section */}
      {shipmentType === 'pallets' && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Pallet Details</h3>
            <button
              type="button"
              onClick={() => {
                setCurrentPallet({
                  label: '',
                  type: '', // Will be set in modal
                  dimensions: { length: '', width: '', height: '' },
                  weight: '',
                  notes: '',
                  items: [], // For multi SKU pallets
                  productSku: '', // For single SKU pallets
                  internalSku: '',
                  productName: '',
                  packagingType: 'individual', // New field for single SKU packaging
                  quantity: 1,
                  unitOfMeasure: 'each',
                  // For cartons (single-SKU)
                  cartonCount: 1,
                  unitsPerCarton: 1,
                  cartonDimensions: { length: '', width: '', height: '', weight: '' },
                  // For individual items (single-SKU)
                  individualDimensions: { length: '', width: '', height: '', weight: '' },
                  itemDimensions: { length: '', width: '', height: '', weight: '' },
                  receivingInstructions: cloneReceivingInstructions(formik.values.receivingInstructions),
                  serialNumbers: [],
                  macAddresses: [],
                  depalletizeOnArrival: false,
                  crossDock: false,
                  // Cross-dock specific fields
                  crossDockShippingArrangement: '', // 'customer_arranges' or 'warehouse_arranges'
                  crossDockDestination: {
                    useExistingAddress: false,
                    selectedAddressId: '',
                    newAddress: {
                      name: '',
                      street_address: '',
                      address_line_2: '',
                      city: '',
                      state_province: '',
                      postal_code: '',
                      country: 'Canada',
                      contact_name: '',
                      contact_phone: '',
                      notes: ''
                    }
                  },
                  crossDockShippingDetails: {
                    carrierPreference: '',
                    serviceLevel: '',
                    specialInstructions: ''
                  },
                  crossDockDocuments: {
                    providedNow: false,
                    bolNumber: '',
                    trackingNumber: '',
                    documentNotes: ''
                  },
                  cartonInfo: {
                    hasCartons: false,
                    cartonsPerPallet: 1,
                    dimensions: { length: '', width: '', height: '', weight: '' }
                  }
                });
                setShowAddPallet(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <FiPlus className="h-4 w-4 mr-2" />
              Add Pallet
            </button>
          </div>

          {/* Existing Pallets List */}
          {pallets.length > 0 && (
            <div className="space-y-4 mb-6">
              {pallets.map((pallet, index) => (
                <div key={index} className="bg-white border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="font-medium text-gray-900 mr-2">{pallet.label}</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          pallet.type === 'single' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {pallet.type === 'single' ? 'Single SKU' : 'Multi SKU'}
                        </span>
                      </div>
                      {pallet.type === 'single' ? (
                        <p className="text-sm text-gray-600">
                          {pallet.productName} ({pallet.productSku}) - Qty: {pallet.quantity} {pallet.unitOfMeasure}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600">
                          {pallet.items?.length || 0} different items
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {pallet.dimensions?.length || 0}×{pallet.dimensions?.width || 0}×{pallet.dimensions?.height || 0} | {pallet.weight || 0} lbs
                      </p>
                      
                      {/* Asset Status Indicators */}
                      {(() => {
                        const assetStatus = getAssetStatus(pallet, 'pallet');
                        const serialDisplay = getStatusDisplay(assetStatus.serialStatus);
                        const macDisplay = getStatusDisplay(assetStatus.macStatus);
                        
                        return (
                          <div className="flex gap-4 mt-1">
                            <span className={`inline-flex items-center text-xs ${serialDisplay.color}`}>
                              {serialDisplay.icon && <serialDisplay.icon className="h-3.5 w-3.5 mr-1" />} SNs: {assetStatus.serialCount}/{assetStatus.expectedQty}
                            </span>
                            <span className={`inline-flex items-center text-xs ${macDisplay.color}`}>
                              {macDisplay.icon && <macDisplay.icon className="h-3.5 w-3.5 mr-1" />} MACs: {assetStatus.macCount}/{assetStatus.expectedQty}
                            </span>
                          </div>
                        );
                      })()}
                      
                      {/* Show warehouse instructions if any are set */}
                      {(pallet.depalletizeOnArrival || pallet.crossDock) && (
                        <div className="flex gap-2 mt-2">
                          {pallet.depalletizeOnArrival && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <LuBox className="h-3.5 w-3.5 mr-1" />
                              Depalletize
                            </span>
                          )}
                          {pallet.crossDock && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <LuTruck className="h-3.5 w-3.5 mr-1" />
                              Cross-dock
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          // Handle both locally created pallets and database-loaded pallets
                          let normalizedPallet;
                          
                          if (pallet.PalletItems) {
                            // This is a database-loaded pallet with associations
                            const palletItems = (pallet.PalletItems || []).map(palletItem => {
                              const requestLineItem = palletItem.RequestLineItem || {};
                              const assets = palletItem.Assets || [];
                              
                              return {
                                productSku: requestLineItem.productSku || '',
                                productName: requestLineItem.productName || '',
                                quantity: palletItem.quantity_on_pallet || requestLineItem.quantity || 1,
                                unitOfMeasure: requestLineItem.unitOfMeasure || 'each',
                                serialNumbers: assets.filter(a => a.serialNumber).map(a => a.serialNumber),
                                macAddresses: assets.filter(a => a.macAddress).map(a => a.macAddress),
                                notes: requestLineItem.notes || ''
                              };
                            });
                            
                            normalizedPallet = {
                              ...pallet,
                              // Map backend fields to frontend field names
                              label: pallet.pallet_label || pallet.label || '',
                              type: pallet.type || 'single',
                              dimensions: pallet.dimensions || { length: '', width: '', height: '' },
                              weight: pallet.weight_lbs || pallet.weight || '',
                              notes: pallet.notes || '',
                              items: palletItems,
                              productSku: palletItems.length === 1 ? palletItems[0].productSku : '',
                              productName: palletItems.length === 1 ? palletItems[0].productName : '',
                              quantity: palletItems.length === 1 ? palletItems[0].quantity : 1,
                              unitOfMeasure: palletItems.length === 1 ? palletItems[0].unitOfMeasure : 'each',
                              serialNumbers: palletItems.length === 1 ? palletItems[0].serialNumbers : [],
                              macAddresses: palletItems.length === 1 ? palletItems[0].macAddresses : [],
                              depalletizeOnArrival: pallet.depalletize_on_arrival || pallet.depalletizeOnArrival || false,
                              crossDock: pallet.cross_dock || pallet.crossDock || false
                            };
                          } else {
                            // This is a locally created pallet - use data as-is but ensure all fields exist
                            normalizedPallet = {
                              ...pallet,
                              // Ensure all required fields exist with defaults
                              label: pallet.label || '',
                              type: pallet.type || 'single',
                              dimensions: pallet.dimensions || { length: '', width: '', height: '' },
                              weight: pallet.weight || '',
                              notes: pallet.notes || '',
                              items: pallet.items || [],
                              productSku: pallet.productSku || '',
                              productName: pallet.productName || '',
                              quantity: pallet.quantity || 1,
                              unitOfMeasure: pallet.unitOfMeasure || 'each',
                              packagingType: pallet.packagingType || 'individual',
                              cartonCount: pallet.cartonCount || 1,
                              unitsPerCarton: pallet.unitsPerCarton || 1,
                              cartonDimensions: pallet.cartonDimensions || { length: '', width: '', height: '', weight: '' },
                              itemDimensions: pallet.itemDimensions || { length: '', width: '', height: '', weight: '' },
                              serialNumbers: pallet.serialNumbers || [],
                              macAddresses: pallet.macAddresses || [],
                              depalletizeOnArrival: pallet.depalletizeOnArrival || false,
                              crossDock: pallet.crossDock || false
                            };
                          }
                          
                          // Ensure cross-dock fields exist
                          normalizedPallet.crossDockShippingArrangement = normalizedPallet.crossDockShippingArrangement || '';
                          normalizedPallet.crossDockDestination = normalizedPallet.crossDockDestination || {
                            useExistingAddress: false,
                            selectedAddressId: '',
                            newAddress: {
                              name: '',
                              street_address: '',
                              address_line_2: '',
                              city: '',
                              state_province: '',
                              postal_code: '',
                              country: 'Canada',
                              contact_name: '',
                              contact_phone: '',
                              notes: ''
                            }
                          };
                          normalizedPallet.crossDockShippingDetails = normalizedPallet.crossDockShippingDetails || {
                            carrierPreference: '',
                            serviceLevel: '',
                            specialInstructions: ''
                          };
                          normalizedPallet.crossDockDocuments = normalizedPallet.crossDockDocuments || {
                            providedNow: false,
                            bolNumber: '',
                            trackingNumber: '',
                            documentNotes: ''
                          };
                          normalizedPallet.cartonInfo = normalizedPallet.cartonInfo || {
                            hasCartons: false,
                            cartonsPerPallet: 1,
                            dimensions: { length: '', width: '', height: '', weight: '' }
                          };
                          
                          setCurrentPallet(normalizedPallet);
                          setShowAddPallet(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-800"
                        title="Edit pallet"
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Clone the pallet with a new label
                          const clonedPallet = {
                            ...pallet,
                            label: `${pallet.label || 'Pallet'} - Copy`,
                            // Clear serial numbers and MAC addresses for the clone
                            serialNumbers: [],
                            macAddresses: [],
                            // If it's a multi-SKU pallet, clear assets from items too
                            items: pallet.items ? pallet.items.map(item => ({
                              ...item,
                              serialNumbers: [],
                              macAddresses: []
                            })) : []
                          };
                          setPallets(prev => [...prev, clonedPallet]);
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="Clone pallet"
                      >
                        <FiCopy className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (pallets.length === 1) {
                            alert('Cannot delete the last pallet. An ASN request must have at least one pallet or carton.');
                            return;
                          }
                          setPallets(pallets.filter((_, i) => i !== index));
                        }}
                        className={`${pallets.length === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                        disabled={pallets.length === 1}
                        title={pallets.length === 1 ? 'Cannot delete the last pallet' : 'Delete pallet'}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pallets.length === 0 && (
            <div className="text-center py-12 bg-white border-2 border-dashed border-gray-300 rounded-lg">
              <FiBox className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No pallets added</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first pallet.</p>
            </div>
          )}
        </div>
      )}

      {/* Carton Management Section */}
      {shipmentType === 'cartons' && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Carton Details</h3>
            <button
              type="button"
              onClick={() => {
                setCurrentCarton({
                  productSku: '',
                  internalSku: '',
                  productName: '',
                  cartonCount: 1,
                  unitsPerCarton: 1,
                  quantity: 1,
                  unitOfMeasure: 'each',
                  cartonDimensions: { length: '', width: '', height: '', weight: '' },
                  itemDimensions: { length: '', width: '', height: '', weight: '' },
                  receivingInstructions: cloneReceivingInstructions(formik.values.receivingInstructions),
                  serialNumbers: [],
                  macAddresses: [],
                  notes: '',
                  // Cross-dock fields for cartons
                  crossDock: false,
                  crossDockShippingArrangement: '', // 'customer_arranges' or 'warehouse_arranges'
                  crossDockDestination: {
                    useExistingAddress: false,
                    selectedAddressId: '',
                    newAddress: {
                      name: '',
                      street_address: '',
                      address_line_2: '',
                      city: '',
                      state_province: '',
                      postal_code: '',
                      country: 'Canada',
                      contact_name: '',
                      contact_phone: '',
                      notes: ''
                    }
                  },
                  crossDockShippingDetails: {
                    carrierPreference: '',
                    serviceLevel: '',
                    specialInstructions: ''
                  },
                  crossDockDocuments: {
                    providedNow: false,
                    bolNumber: '',
                    trackingNumber: '',
                    documentNotes: ''
                  }
                });
                setShowAddCarton(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <FiPlus className="h-4 w-4 mr-2" />
              Add Carton
            </button>
          </div>

          {/* Existing Cartons List */}
          {cartons.length > 0 && (
            <div className="space-y-4 mb-6">
              {cartons.map((carton, index) => (
                <div key={index} className="bg-white border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {carton.productName} ({carton.productSku})
                      </h4>
                      <p className="text-sm text-gray-600">
                        {carton.cartonCount} cartons × {carton.unitsPerCarton} units = {carton.quantity} total {carton.unitOfMeasure}
                      </p>
                      <p className="text-xs text-gray-500">
                        Carton: {carton.cartonDimensions?.length || 0}x{carton.cartonDimensions?.width || 0}x{carton.cartonDimensions?.height || 0}" | {carton.cartonDimensions?.weight || 0} lbs
                      </p>
                      <p className="text-xs text-gray-400">
                        Item: {carton.itemDimensions?.length || 0}x{carton.itemDimensions?.width || 0}x{carton.itemDimensions?.height || 0}" | {carton.itemDimensions?.weight || 0} lbs each
                      </p>
                      
                      {/* Asset Status Indicators */}
                      {(() => {
                        const assetStatus = getAssetStatus(carton, 'carton');
                        const serialDisplay = getStatusDisplay(assetStatus.serialStatus);
                        const macDisplay = getStatusDisplay(assetStatus.macStatus);
                        
                        return (
                          <div className="flex gap-4 mt-1">
                            <span className={`inline-flex items-center text-xs ${serialDisplay.color}`}>
                              {serialDisplay.icon && <serialDisplay.icon className="h-3.5 w-3.5 mr-1" />} SNs: {assetStatus.serialCount}/{assetStatus.expectedQty}
                            </span>
                            <span className={`inline-flex items-center text-xs ${macDisplay.color}`}>
                              {macDisplay.icon && <macDisplay.icon className="h-3.5 w-3.5 mr-1" />} MACs: {assetStatus.macCount}/{assetStatus.expectedQty}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentCarton(carton);
                          setShowAddCarton(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-800"
                        title="Edit carton"
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Clone the carton with a new label
                          const clonedCarton = {
                            ...carton,
                            productName: `${carton.productName} - Copy`,
                            // Clear serial numbers and MAC addresses for the clone
                            serialNumbers: [],
                            macAddresses: [],
                            // Generate new ID for the clone
                            id: Date.now()
                          };
                          setCartons(prev => [...prev, clonedCarton]);
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="Clone carton"
                      >
                        <FiCopy className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (cartons.length === 1) {
                            alert('Cannot delete the last carton. An ASN request must have at least one pallet or carton.');
                            return;
                          }
                          setCartons(cartons.filter((_, i) => i !== index));
                        }}
                        className={`${cartons.length === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                        disabled={cartons.length === 1}
                        title={cartons.length === 1 ? 'Cannot delete the last carton' : 'Delete carton'}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {cartons.length === 0 && (
            <div className="text-center py-12 bg-white border-2 border-dashed border-gray-300 rounded-lg">
              <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No cartons added</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first carton.</p>
            </div>
          )}
        </div>
      )}

      {/* Pallet Add/Edit Modal */}
      {showAddPallet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {currentPallet.type ? 
                  (currentPallet.type === 'single' ? 'Edit Single SKU Pallet' : 'Edit Multi SKU Pallet') : 
                  'Configure Pallet'
                }
              </h3>
              
              {/* Pallet Type Selection - Only show if type not yet selected (new pallet) */}
              {!currentPallet.type && !currentPallet.id && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Pallet Type *</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className="border-2 rounded-lg p-4 cursor-pointer transition-all border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      onClick={() => setCurrentPallet(prev => ({ ...prev, type: 'single' }))}
                    >
                      <div className="text-center">
                        <FiBox className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                        <h4 className="text-sm font-medium text-gray-900">Single SKU</h4>
                        <p className="text-xs text-gray-500 mt-1">Pallet contains the same product</p>
                      </div>
                    </div>
                    
                    <div
                      className="border-2 rounded-lg p-4 cursor-pointer transition-all border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      onClick={() => setCurrentPallet(prev => ({ ...prev, type: 'multi' }))}
                    >
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          <FiBox className="h-6 w-6 text-blue-500" />
                          <FiPackage className="h-4 w-4 text-green-500 -ml-2" />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">Multi SKU</h4>
                        <p className="text-xs text-gray-500 mt-1">Pallet contains multiple different products</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}



              {/* Show type badge if type is selected */}
              {currentPallet.type && (
                <div className="mb-4 flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    currentPallet.type === 'single' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {currentPallet.type === 'single' ? 'Single SKU Pallet' : 'Multi SKU Pallet'}
                  </span>
                  {/* Only show "Change Type" for new pallets, not when editing existing ones */}
                  {!currentPallet.label && (
                    <button
                      type="button"
                      onClick={() => setCurrentPallet(prev => ({ ...prev, type: '', items: [], productSku: '', internalSku: '', productName: '', quantity: 1 }))}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Change Type
                    </button>
                  )}
                </div>
              )}
              
              {/* Pallet Configuration Form - Only show after type is selected */}
              {currentPallet.type && (
                <>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Column - Basic Info */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pallet Label</label>
                        <input
                          type="text"
                          value={currentPallet.label || ''}
                          onChange={(e) => setCurrentPallet(prev => ({ ...prev, label: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pallet Dimensions (inches)</label>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          <input
                            type="number"
                            placeholder="L"
                            value={currentPallet.dimensions?.length || ''}
                            onChange={(e) => setCurrentPallet(prev => ({
                              ...prev,
                              dimensions: { ...prev.dimensions || {}, length: e.target.value }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <input
                            type="number"
                            placeholder="W"
                            value={currentPallet.dimensions?.width || ''}
                            onChange={(e) => setCurrentPallet(prev => ({
                              ...prev,
                              dimensions: { ...prev.dimensions || {}, width: e.target.value }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <input
                            type="number"
                            placeholder="H"
                            value={currentPallet.dimensions?.height || ''}
                            onChange={(e) => setCurrentPallet(prev => ({
                              ...prev,
                              dimensions: { ...prev.dimensions || {}, height: e.target.value }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Pallet Weight (lbs)</label>
                          <input
                            type="number"
                            value={currentPallet.weight || ''}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, weight: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 flex items-center">
                            Approx. Value ($)
                            <span className="relative group ml-1">
                              <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                Approximate value for insurance purposes
                              </span>
                            </span>
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={currentPallet.declaredValue || ''}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, declaredValue: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Total value of this pallet"
                          />
                        </div>
                      </div>

                      {/* Project Name / Code */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Project Name</label>
                          <input
                            type="text"
                            value={currentPallet.projectName || ''}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, projectName: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="e.g., Bank of Canada"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Project Code</label>
                          <input
                            type="text"
                            value={currentPallet.projectCode || ''}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, projectCode: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="e.g., BOC12345"
                          />
                        </div>
                      </div>

                      {/* Warehouse Instructions */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">Warehouse Instructions</label>
                        
                        <div className="flex items-center">
                          <input
                            id={`depalletize-${currentPallet.label || 'new'}`}
                            type="checkbox"
                            checked={currentPallet.depalletizeOnArrival || false}
                            onChange={(e) => setCurrentPallet(prev => ({ 
                              ...prev, 
                              depalletizeOnArrival: e.target.checked 
                            }))}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`depalletize-${currentPallet.label || 'new'}`} className="ml-2 block text-sm text-gray-700">
                            <span className="font-medium">Depalletize on arrival</span>
                            <div className="text-xs text-gray-500">Break down this pallet and store items individually</div>
                          </label>
                        </div>



                        {/* Cross-dock Options */}
                        {currentPallet.crossDock && (
                          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-md space-y-4">
                            <h4 className="text-sm font-medium text-purple-900">Cross-dock Shipping Arrangement</h4>
                            
                            {/* Shipping Arrangement Selection */}
                            <div className="space-y-2">
                              <div className="flex items-center">
                                <input
                                  id="customer-arranges"
                                  type="radio"
                                  name={`shipping-arrangement-${currentPallet.label || 'new'}`}
                                  value="customer_arranges"
                                  checked={currentPallet.crossDockShippingArrangement === 'customer_arranges'}
                                  onChange={(e) => setCurrentPallet(prev => ({
                                    ...prev,
                                    crossDockShippingArrangement: e.target.value
                                  }))}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                />
                                <label htmlFor="customer-arranges" className="ml-2 text-sm text-gray-700">
                                  <span className="font-medium">I will arrange the outbound shipping from your warehouse</span>
                                  <div className="text-xs text-gray-500">You'll provide BOL and shipping labels</div>
                                </label>
                              </div>
                              
                              <div className="flex items-center">
                                <input
                                  id="warehouse-arranges"
                                  type="radio"
                                  name={`shipping-arrangement-${currentPallet.label || 'new'}`}
                                  value="warehouse_arranges"
                                  checked={currentPallet.crossDockShippingArrangement === 'warehouse_arranges'}
                                  onChange={(e) => setCurrentPallet(prev => ({
                                    ...prev,
                                    crossDockShippingArrangement: e.target.value
                                  }))}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                />
                                <label htmlFor="warehouse-arranges" className="ml-2 text-sm text-gray-700">
                                  <span className="font-medium">Please arrange outbound shipping on my behalf</span>
                                  <div className="text-xs text-gray-500">We'll handle carrier selection and shipping</div>
                                </label>
                              </div>
                            </div>

                            {/* Customer Arranges - Documents */}
                            {currentPallet.crossDockShippingArrangement === 'customer_arranges' && (
                              <div className="space-y-3">
                                <h5 className="text-sm font-medium text-gray-700">Shipping Documents</h5>
                                
                                <div className="flex items-center">
                                  <input
                                    id="provide-now"
                                    type="radio"
                                    name={`document-timing-${currentPallet.label || 'new'}`}
                                    checked={currentPallet.crossDockDocuments.providedNow === true}
                                    onChange={() => setCurrentPallet(prev => ({
                                      ...prev,
                                      crossDockDocuments: { ...prev.crossDockDocuments, providedNow: true }
                                    }))}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                  />
                                  <label htmlFor="provide-now" className="ml-2 text-sm text-gray-700">
                                    Provide BOL/tracking information now
                                  </label>
                                </div>
                                
                                <div className="flex items-center">
                                  <input
                                    id="provide-later"
                                    type="radio"
                                    name={`document-timing-${currentPallet.label || 'new'}`}
                                    checked={currentPallet.crossDockDocuments.providedNow === false}
                                    onChange={() => setCurrentPallet(prev => ({
                                      ...prev,
                                      crossDockDocuments: { ...prev.crossDockDocuments, providedNow: false }
                                    }))}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                  />
                                  <label htmlFor="provide-later" className="ml-2 text-sm text-gray-700">
                                    I'll provide documents later
                                  </label>
                                </div>

                                {currentPallet.crossDockDocuments.providedNow && (
                                  <div className="space-y-3 pl-4 border-l-2 border-purple-200">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700">BOL Number</label>
                                      <input
                                        type="text"
                                        value={currentPallet.crossDockDocuments.bolNumber}
                                        onChange={(e) => setCurrentPallet(prev => ({
                                          ...prev,
                                          crossDockDocuments: { ...prev.crossDockDocuments, bolNumber: e.target.value }
                                        }))}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="Bill of Lading number"
                                      />
                                    </div>
                                    
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700">Tracking Number</label>
                                      <input
                                        type="text"
                                        value={currentPallet.crossDockDocuments.trackingNumber}
                                        onChange={(e) => setCurrentPallet(prev => ({
                                          ...prev,
                                          crossDockDocuments: { ...prev.crossDockDocuments, trackingNumber: e.target.value }
                                        }))}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="Carrier tracking number"
                                      />
                                    </div>
                                    
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700">Document Notes</label>
                                      <textarea
                                        rows="2"
                                        value={currentPallet.crossDockDocuments.documentNotes}
                                        onChange={(e) => setCurrentPallet(prev => ({
                                          ...prev,
                                          crossDockDocuments: { ...prev.crossDockDocuments, documentNotes: e.target.value }
                                        }))}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="Additional notes about shipping documents..."
                                      />
                                    </div>
                                    
                                    <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                                      Document upload functionality will be available soon. For now, please provide reference numbers and we'll coordinate document transfer separately.
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Warehouse Arranges - Destination & Shipping Details */}
                            {currentPallet.crossDockShippingArrangement === 'warehouse_arranges' && (
                              <div className="space-y-4">
                                <h5 className="text-sm font-medium text-gray-700">Destination & Shipping Details</h5>
                                
                                {/* Destination Address Selection */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Destination Address</label>
                                  
                                  <div className="flex items-center mb-3">
                                    <input
                                      id="use-existing-address"
                                      type="radio"
                                      name={`address-type-${currentPallet.label || 'new'}`}
                                      checked={currentPallet.crossDockDestination.useExistingAddress === true}
                                      onChange={() => setCurrentPallet(prev => ({
                                        ...prev,
                                        crossDockDestination: { ...prev.crossDockDestination, useExistingAddress: true }
                                      }))}
                                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                    />
                                    <label htmlFor="use-existing-address" className="ml-2 text-sm text-gray-700">
                                      Use existing address
                                    </label>
                                  </div>
                                  
                                  <div className="flex items-center mb-3">
                                    <input
                                      id="add-new-address"
                                      type="radio"
                                      name={`address-type-${currentPallet.label || 'new'}`}
                                      checked={currentPallet.crossDockDestination.useExistingAddress === false}
                                      onChange={() => setCurrentPallet(prev => ({
                                        ...prev,
                                        crossDockDestination: { ...prev.crossDockDestination, useExistingAddress: false }
                                      }))}
                                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                                    />
                                    <label htmlFor="add-new-address" className="ml-2 text-sm text-gray-700">
                                      Enter new address
                                    </label>
                                  </div>

                                  {currentPallet.crossDockDestination.useExistingAddress ? (
                                    <select
                                      value={currentPallet.crossDockDestination.selectedAddressId}
                                      onChange={(e) => setCurrentPallet(prev => ({
                                        ...prev,
                                        crossDockDestination: { ...prev.crossDockDestination, selectedAddressId: e.target.value }
                                      }))}
                                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    >
                                      <option value="">Select destination address</option>
                                      {customerWarehouses.map((warehouse) => (
                                        <option key={warehouse.id} value={warehouse.id}>
                                          {warehouse.name} - {warehouse.street_address}, {warehouse.city}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-purple-200">
                                      <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Company/Location Name</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.name}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, name: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="Destination company name"
                                        />
                                      </div>
                                      
                                      <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Street Address</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.street_address}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, street_address: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="Street address"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">City</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.city}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, city: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="City"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">State/Province</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.state_province}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, state_province: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="State/Province"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.postal_code}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, postal_code: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="Postal code"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">Country</label>
                                        <select
                                          value={currentPallet.crossDockDestination.newAddress.country}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, country: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        >
                                          {COUNTRIES.map(country => (
                                            <option key={country.value} value={country.value}>
                                              {country.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.contact_name}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, contact_name: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="Contact person"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                                        <input
                                          type="text"
                                          value={currentPallet.crossDockDestination.newAddress.contact_phone}
                                          onChange={(e) => setCurrentPallet(prev => ({
                                            ...prev,
                                            crossDockDestination: {
                                              ...prev.crossDockDestination,
                                              newAddress: { ...prev.crossDockDestination.newAddress, contact_phone: e.target.value }
                                            }
                                          }))}
                                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                          placeholder="Phone number"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Shipping Preferences */}
                                <div className="space-y-3">
                                  <h6 className="text-sm font-medium text-gray-700">Shipping Preferences</h6>
                                  
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700">Carrier Preference</label>
                                      <select
                                        value={currentPallet.crossDockShippingDetails.carrierPreference}
                                        onChange={(e) => setCurrentPallet(prev => ({
                                          ...prev,
                                          crossDockShippingDetails: { ...prev.crossDockShippingDetails, carrierPreference: e.target.value }
                                        }))}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                      >
                                        <option value="">Select carrier (optional)</option>
                                        <option value="fedex">FedEx</option>
                                        <option value="ups">UPS</option>
                                        <option value="purolator">Purolator</option>
                                        <option value="canada_post">Canada Post</option>
                                        <option value="dhl">DHL</option>
                                        <option value="best_rate">Best Rate (Let us choose)</option>
                                      </select>
                                    </div>
                                    
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700">Service Level</label>
                                      <select
                                        value={currentPallet.crossDockShippingDetails.serviceLevel}
                                        onChange={(e) => setCurrentPallet(prev => ({
                                          ...prev,
                                          crossDockShippingDetails: { ...prev.crossDockShippingDetails, serviceLevel: e.target.value }
                                        }))}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                      >
                                        <option value="">Select service level</option>
                                        <option value="standard">Standard</option>
                                        <option value="expedited">Expedited</option>
                                        <option value="overnight">Overnight</option>
                                        <option value="two_day">2-Day</option>
                                        <option value="ground">Ground</option>
                                      </select>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                                    <textarea
                                      rows="2"
                                      value={currentPallet.crossDockShippingDetails.specialInstructions}
                                      onChange={(e) => setCurrentPallet(prev => ({
                                        ...prev,
                                        crossDockShippingDetails: { ...prev.crossDockShippingDetails, specialInstructions: e.target.value }
                                      }))}
                                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                      placeholder="Any special shipping requirements or delivery instructions..."
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>



                  {/* Right Column - Product Info for Single SKU */}
                  {currentPallet.type === 'single' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Product SKU *</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={currentPallet.productSku}
                              onChange={(e) => {
                                const term = e.target.value;
                                setCurrentPallet(prev => ({ ...prev, productSku: term }));
                                
                                if (term.length > 1 && searchResults.length > 0) {
                                  const filteredResults = searchResults.filter(item =>
                                    item.productSku.toLowerCase().includes(term.toLowerCase()) ||
                                    item.productName.toLowerCase().includes(term.toLowerCase())
                                  );
                                  setShowProductDropdown(filteredResults.length > 0);
                                } else if (term.length > 1) {
                                  searchCustomerInventory(term);
                                } else {
                                  setShowProductDropdown(false);
                                }
                              }}
                              onFocus={() => fetchProducts()}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              placeholder="Search by SKU or name"
                            />
                            {showProductDropdown && (
                              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1">
                                {searchResults.length > 0 ? (
                                  searchResults.filter(item =>
                                    item.productSku.toLowerCase().includes(currentPallet.productSku.toLowerCase()) ||
                                    item.productName.toLowerCase().includes(currentPallet.productSku.toLowerCase())
                                  ).map(product => (
                                    <button
                                      key={product.id}
                                      onClick={() => {
                                        setCurrentPallet(prev => ({
                                          ...prev,
                                          productSku: product.productSku,
                                          internalSku: product.internalSku || prev.internalSku,
                                          productName: product.productName,
                                          unitOfMeasure: product.unitOfMeasurement || prev.unitOfMeasure,
                                          // Prefill dimensions and weight if available
                                          individualDimensions: {
                                            ...prev.individualDimensions,
                                            length: product.length || prev.individualDimensions?.length || '',
                                            width: product.width || prev.individualDimensions?.width || '',
                                            height: product.height || prev.individualDimensions?.height || '',
                                            weight: product.weightLbs || prev.individualDimensions?.weight || ''
                                          }
                                        }));
                                        setShowProductDropdown(false);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                    >
                                      <div className="font-medium">{product.productSku}{product.internalSku ? ` (${product.internalSku})` : ''}</div>
                                      <div className="text-gray-500 text-xs">{product.productName}</div>
                                      {product.quantity && (
                                        <div className="text-gray-400 text-xs">Current stock: {product.quantity}</div>
                                      )}
                                    </button>
                                  ))
                                ) : currentPallet.productSku.length > 1 ? (
                                  <div className="px-4 py-2 text-sm text-gray-700">
                                    <div className="text-gray-500">No existing items found</div>
                                    <div className="text-xs text-gray-400 mt-1">You can create a new item with this SKU</div>
                                  </div>
                                ) : (
                                  <div className="px-4 py-2 text-sm text-gray-700">Type to search existing items</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Product Name *</label>
                          <input
                            type="text"
                            value={currentPallet.productName}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, productName: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Enter product name"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Internal SKU <span className="text-xs font-normal text-gray-500">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            value={currentPallet.internalSku}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, internalSku: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Internal part number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Packaging Type *</label>
                          <select
                            value={currentPallet.packagingType}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, packagingType: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          >
                            <option value="individual">Individual Units</option>
                            <option value="carton">Cartons</option>
                            <option value="multipack">Multipacks</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Unit of Measure</label>
                          <select
                            value={currentPallet.unitOfMeasure}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, unitOfMeasure: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          >
                            <option value="each">Each</option>
                            <option value="box">Box</option>
                            <option value="case">Case</option>
                            <option value="pack">Pack</option>
                            <option value="pallet">Pallet</option>
                          </select>
                        </div>
                      </div>

                      {/* Carton Details */}
                      {currentPallet.packagingType === 'carton' && (
                        <div className="space-y-4">
                          {/* Basic Carton Info */}
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Carton Details</h5>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Number of Cartons *</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={currentPallet.cartonCount}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    cartonCount: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                                  }))}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Units per Carton *</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={currentPallet.unitsPerCarton}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    unitsPerCarton: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                                  }))}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Carton Dimensions */}
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                            <h5 className="text-sm font-medium text-gray-800 mb-3">Carton Dimensions & Weight</h5>
                            <div className="grid grid-cols-4 gap-4">
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Length (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.cartonDimensions?.length || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    cartonDimensions: { 
                                      ...prev.cartonDimensions, 
                                      length: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Width (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.cartonDimensions?.width || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    cartonDimensions: { 
                                      ...prev.cartonDimensions, 
                                      width: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Height (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.cartonDimensions?.height || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    cartonDimensions: { 
                                      ...prev.cartonDimensions, 
                                      height: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Weight (lbs) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.cartonDimensions?.weight || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    cartonDimensions: { 
                                      ...prev.cartonDimensions, 
                                      weight: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Individual Item Dimensions */}
                          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                            <h5 className="text-sm font-medium text-gray-800 mb-3">Item Dimensions & Weight</h5>
                            <div className="grid grid-cols-4 gap-4">
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Length (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.itemDimensions?.length || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    itemDimensions: { 
                                      ...prev.itemDimensions, 
                                      length: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Width (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.itemDimensions?.width || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    itemDimensions: { 
                                      ...prev.itemDimensions, 
                                      width: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Height (in) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.itemDimensions?.height || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    itemDimensions: { 
                                      ...prev.itemDimensions, 
                                      height: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                              <div className="flex flex-col">
                                <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Weight (lbs) *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentPallet.itemDimensions?.weight || ''}
                                  onChange={(e) => setCurrentPallet(prev => ({ 
                                    ...prev, 
                                    itemDimensions: { 
                                      ...prev.itemDimensions, 
                                      weight: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                                    } 
                                  }))}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Individual Items Details */}
                      {currentPallet.packagingType === 'individual' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            value={currentPallet.quantity}
                            onChange={(e) => setCurrentPallet(prev => ({
                              ...prev,
                              quantity: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                            }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">Unit Dimensions & Weight</label>
                            <div className="mt-1 grid grid-cols-4 gap-2">
                              <input
                                type="number"
                                placeholder="Length"
                                value={currentPallet.individualDimensions?.length || ''}
                                onChange={(e) => setCurrentPallet(prev => ({ 
                                  ...prev, 
                                  individualDimensions: { 
                                    ...prev.individualDimensions, 
                                    length: e.target.value 
                                  } 
                                }))}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <input
                                type="number"
                                placeholder="Width"
                                value={currentPallet.individualDimensions?.width || ''}
                                onChange={(e) => setCurrentPallet(prev => ({ 
                                  ...prev, 
                                  individualDimensions: { 
                                    ...prev.individualDimensions, 
                                    width: e.target.value 
                                  } 
                                }))}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <input
                                type="number"
                                placeholder="Height"
                                value={currentPallet.individualDimensions?.height || ''}
                                onChange={(e) => setCurrentPallet(prev => ({ 
                                  ...prev, 
                                  individualDimensions: { 
                                    ...prev.individualDimensions, 
                                    height: e.target.value 
                                  } 
                                }))}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <input
                                type="number"
                                placeholder="Weight (lbs)"
                                value={currentPallet.individualDimensions?.weight || ''}
                                onChange={(e) => setCurrentPallet(prev => ({ 
                                  ...prev, 
                                  individualDimensions: { 
                                    ...prev.individualDimensions, 
                                    weight: e.target.value 
                                  } 
                                }))}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Multipack Details */}
                      {currentPallet.packagingType === 'multipack' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            value={currentPallet.quantity}
                            onChange={(e) => setCurrentPallet(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      )}

                      {/* Asset Tracking Buttons */}
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => openAssetModal('currentPallet', 'serialNumbers')}
                          className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FiTag className="h-4 w-4 mr-2" />
                          Serial Numbers ({currentPallet.serialNumbers.length})
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => openAssetModal('currentPallet', 'macAddresses')}
                          className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FiWifi className="h-4 w-4 mr-2" />
                          MAC Addresses ({currentPallet.macAddresses.length})
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-xs text-green-700">
                          {currentPallet.serialNumbers.length > 0
                            ? `Serial Numbers Added: ${currentPallet.serialNumbers.length}`
                            : 'No serial numbers added yet'}
                        </div>
                        <div className="text-xs text-green-700">
                          {currentPallet.macAddresses.length > 0
                            ? `MAC Addresses Added: ${currentPallet.macAddresses.length}`
                            : 'No MAC addresses added yet'}
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          rows="3"
                          value={currentPallet.notes}
                          onChange={(e) => setCurrentPallet(prev => ({ ...prev, notes: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="Any special notes for this item..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Multi SKU Management */}
                  {currentPallet.type === 'multi' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700">Items on Pallet</label>
                        <button
                          type="button"
                          onClick={() => {
                            // Clear editing state and show add item modal
                            setEditingItemIndex(null);
                            setCurrentItem({
                              productSku: '',
                              internalSku: '',
                              productName: '',
                              packagingType: 'individual',
                              unitOfMeasure: 'each',
                              cartonCount: 1,
                              unitsPerCarton: 1,
                              quantity: 1,
                              dimensions: { length: '', width: '', height: '', weight: '' },
                              serialNumbers: [],
                              macAddresses: [],
                              notes: ''
                            });
                            setShowAddItem(true);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FiPlus className="h-4 w-4 mr-1" />
                          Add Item
                        </button>
                      </div>
                      
                      {(!currentPallet.items || currentPallet.items.length === 0) ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <FiPackage className="mx-auto h-8 w-8 text-gray-400" />
                          <p className="mt-2 text-sm text-gray-500">No items added yet</p>
                          <p className="text-xs text-gray-400">Click "Add Item" to start</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {(currentPallet.items || []).map((item, index) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                              <div>
                                <p className="text-sm font-medium">{item.productName}</p>
                                <p className="text-xs text-gray-500">
                                  {item.productSku} - 
                                  {item.packagingType === 'carton' 
                                    ? `${item.cartonCount} cartons × ${item.unitsPerCarton} ${item.unitOfMeasure} = ${item.cartonCount * item.unitsPerCarton} total`
                                    : `Qty: ${item.quantity} ${item.unitOfMeasure}`
                                  }
                                </p>
                                {item.packagingType === 'carton' && item.dimensions && 
                                 (item.dimensions?.length || item.dimensions?.width || item.dimensions?.height || item.dimensions?.weight) && (
                                  <p className="text-xs text-gray-400">
                                    Carton: {item.dimensions?.length || 0}×{item.dimensions?.width || 0}×{item.dimensions?.height || 0}" 
                                    {item.dimensions?.weight && `, ${item.dimensions.weight} lbs`}
                                  </p>
                                )}
                                {item.packagingType === 'individual' && item.dimensions && 
                                 (item.dimensions?.length || item.dimensions?.width || item.dimensions?.height || item.dimensions?.weight) && (
                                  <p className="text-xs text-gray-400">
                                    Unit: {item.dimensions?.length || 0}×{item.dimensions?.width || 0}×{item.dimensions?.height || 0}" 
                                    {item.dimensions?.weight && `, ${item.dimensions.weight} lbs`}
                                  </p>
                                )}
                                
                                {/* Asset Status Indicators for Multi-SKU Items */}
                                {(() => {
                                  const assetStatus = getAssetStatus(item, 'item');
                                  const serialDisplay = getStatusDisplay(assetStatus.serialStatus);
                                  const macDisplay = getStatusDisplay(assetStatus.macStatus);
                                  
                                  return (
                                    <div className="flex gap-4 mt-1">
                                      <span className={`inline-flex items-center text-xs ${serialDisplay.color}`}>
                                        {serialDisplay.icon && <serialDisplay.icon className="h-3.5 w-3.5 mr-1" />} SNs: {assetStatus.serialCount}/{assetStatus.expectedQty}
                                      </span>
                                      <span className={`inline-flex items-center text-xs ${macDisplay.color}`}>
                                        {macDisplay.icon && <macDisplay.icon className="h-3.5 w-3.5 mr-1" />} MACs: {assetStatus.macCount}/{assetStatus.expectedQty}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Load the item data into the currentItem state for editing
                                    setCurrentItem({
                                      ...item,
                                      // Ensure all required fields exist
                                      cartonDimensions: item.cartonDimensions || { length: '', width: '', height: '', weight: '' },
                                      itemDimensions: item.itemDimensions || { length: '', width: '', height: '', weight: '' },
                                      dimensions: item.dimensions || { length: '', width: '', height: '', weight: '' },
                                      serialNumbers: item.serialNumbers || [],
                                      macAddresses: item.macAddresses || []
                                    });
                                    setEditingItemIndex(index);
                                    setShowAddItem(true);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-800"
                                  title="Edit item"
                                >
                                  <FiEdit className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (currentPallet.items.length === 1) {
                                      alert('Cannot delete the last item from a multi-SKU pallet. A pallet must contain at least one item.');
                                      return;
                                    }
                                    setCurrentPallet(prev => ({
                                      ...prev,
                                      items: (prev.items || []).filter((_, i) => i !== index)
                                    }));
                                  }}
                                  className={`${currentPallet.items.length === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                                  disabled={currentPallet.items.length === 1}
                                  title={currentPallet.items.length === 1 ? 'Cannot delete the last item' : 'Delete item'}
                                >
                                  <FiTrash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </>
              )}
              {/* Modal Actions for Pallet Add/Edit */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPallet(false);
                    setCurrentPallet({
                      label: '',
                      type: '',
                      dimensions: { length: '', width: '', height: '' },
                      weight: '',
                      notes: '',
                      items: [],
                      productSku: '',
                      internalSku: '',
                      productName: '',
                      packagingType: 'individual',
                      quantity: 1,
                      unitOfMeasure: 'each',
                      // For cartons (single-SKU)
                      cartonCount: 1,
                      unitsPerCarton: 1,
                      // Separate dimensions for cartons and individual items
                      cartonDimensions: { length: '', width: '', height: '', weight: '' },
                      itemDimensions: { length: '', width: '', height: '', weight: '' },
                      serialNumbers: [],
                      macAddresses: [],
                      depalletizeOnArrival: false,
                      crossDock: false,
                      crossDockShippingArrangement: '',
                      crossDockDestination: {
                        useExistingAddress: false,
                        selectedAddressId: '',
                        newAddress: {
                          name: '',
                          street_address: '',
                          address_line_2: '',
                          city: '',
                          state_province: '',
                          postal_code: '',
                          country: 'Canada',
                          contact_name: '',
                          contact_phone: '',
                          notes: ''
                        }
                      },
                      crossDockShippingDetails: {
                        carrierPreference: '',
                        serviceLevel: '',
                        specialInstructions: ''
                      },
                      crossDockDocuments: {
                        providedNow: false,
                        bolNumber: '',
                        trackingNumber: '',
                        documentNotes: ''
                      },
                      cartonInfo: {
                        hasCartons: false,
                        cartonsPerPallet: 1,
                        dimensions: { length: '', width: '', height: '', weight: '' }
                      }
                    });
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Validate required fields
                    if (!currentPallet.type) {
                      alert('Please select a pallet type');
                      return;
                    }
                    
                    if (currentPallet.type === 'single') {
                      if (!currentPallet.productSku || !currentPallet.productName) {
                        alert('Please fill in Product SKU and Name');
                        return;
                      }
                    }
                    
                    if (currentPallet.type === 'multi' && (!currentPallet.items || currentPallet.items.length === 0)) {
                      alert('Please add at least one item to the pallet');
                      return;
                    }
                    
                    const palletToSave = {
                      ...currentPallet,
                      receivingInstructions: cloneReceivingInstructions(
                        currentPallet.receivingInstructions || formik.values.receivingInstructions
                      )
                    };

                    // Add or update pallet
                    const existingIndex = pallets.findIndex(p => p.label === currentPallet.label);
                    if (existingIndex >= 0) {
                      const updatedPallets = [...pallets];
                      updatedPallets[existingIndex] = palletToSave;
                      setPallets(updatedPallets);
                    } else {
                      setPallets(prev => [...prev, palletToSave]);
                    }
                    
                    setShowAddPallet(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {pallets.some(p => p.label === currentPallet.label) ? 'Update Pallet' : 'Add Pallet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal for Multi SKU Pallets */}
      {showAddItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingItemIndex !== null ? 'Edit Pallet Item' : 'Add Item to Pallet'}
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product SKU *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearch}
                        onFocus={() => fetchProducts()}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Search by SKU or name"
                      />
                      {showProductDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1">
                          {searchResults.length > 0 ? (
                            searchResults.filter(item =>
                              item.productSku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              item.productName.toLowerCase().includes(searchTerm.toLowerCase())
                            ).map(product => (
                              <button
                                key={product.id}
                                onClick={() => handleProductSelect(product)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                              >
                                <div className="font-medium">{product.productSku}</div>
                                <div className="text-gray-500 text-xs">{product.productName}</div>
                                {product.quantity && (
                                  <div className="text-gray-400 text-xs">Current stock: {product.quantity}</div>
                                )}
                              </button>
                            ))
                          ) : searchTerm.length > 1 ? (
                            <div className="px-4 py-2 text-sm text-gray-700">
                              <div className="text-gray-500">No existing items found</div>
                              <div className="text-xs text-gray-400 mt-1">You can create a new item with this SKU</div>
                            </div>
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-700">Type to search existing items</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product Name *</label>
                    <input
                      type="text"
                      value={currentItem.productName}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, productName: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter product name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Internal SKU</label>
                    <input
                      type="text"
                      value={currentItem.internalSku}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, internalSku: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Internal part number (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Packaging Type *</label>
                    <select
                      value={currentItem.packagingType}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, packagingType: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="individual">Individual Units</option>
                      <option value="carton">Cartons</option>
                      <option value="multipack">Multipacks</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Unit of Measure</label>
                    <select
                      value={currentItem.unitOfMeasure}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, unitOfMeasure: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="each">Each</option>
                      <option value="box">Box</option>
                      <option value="case">Case</option>
                      <option value="pack">Pack</option>
                      <option value="pallet">Pallet</option>
                    </select>
                  </div>
                </div>

                {/* Carton Details */}
                {currentItem.packagingType === 'carton' && (
                  <div className="bg-gray-100 p-3 rounded-md space-y-4">
                    {/* Basic Carton Info */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Carton Details</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Number of Cartons *</label>
                          <input
                            type="number"
                            min="1"
                            value={currentItem.cartonCount}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              cartonCount: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                            }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Units per Carton *</label>
                          <input
                            type="number"
                            min="1"
                            value={currentItem.unitsPerCarton}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              unitsPerCarton: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                            }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Carton Dimensions */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <h5 className="text-sm font-medium text-gray-800 mb-3">Carton Dimensions & Weight</h5>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Length (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.cartonDimensions?.length ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              cartonDimensions: {
                                ...prev.cartonDimensions,
                                length: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Width (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.cartonDimensions?.width ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              cartonDimensions: {
                                ...prev.cartonDimensions,
                                width: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Height (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.cartonDimensions?.height ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              cartonDimensions: {
                                ...prev.cartonDimensions,
                                height: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Weight (lbs) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.cartonDimensions?.weight ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              cartonDimensions: {
                                ...prev.cartonDimensions,
                                weight: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Units/Carton *</label>
                          <input
                            type="number"
                            min="1"
                            value={currentItem.unitsPerCarton}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              unitsPerCarton: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 1)
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Individual Item Dimensions */}
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <h5 className="text-sm font-medium text-gray-800 mb-3">Item Dimensions & Weight</h5>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Length (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.itemDimensions?.length ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              itemDimensions: {
                                ...prev.itemDimensions,
                                length: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Width (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.itemDimensions?.width ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              itemDimensions: {
                                ...prev.itemDimensions,
                                width: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Height (in) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.itemDimensions?.height ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              itemDimensions: {
                                ...prev.itemDimensions,
                                height: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1 h-5 whitespace-nowrap">Weight (lbs) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentItem.itemDimensions?.weight ?? ''}
                            onChange={(e) => setCurrentItem(prev => ({
                              ...prev,
                              itemDimensions: {
                                ...prev.itemDimensions,
                                weight: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)
                              }
                            }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Individual Items Details */}
                {currentItem.packagingType === 'individual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700">Unit Dimensions & Weight</label>
                      <div className="mt-1 grid grid-cols-4 gap-2">
                        <input
                          type="number"
                          placeholder="Length"
                          value={currentItem.individualDimensions?.length || ''}
                          onChange={(e) => setCurrentItem(prev => ({ 
                            ...prev, 
                            individualDimensions: { 
                              ...prev.individualDimensions, 
                              length: e.target.value 
                            } 
                          }))}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Width"
                          value={currentItem.individualDimensions?.width || ''}
                          onChange={(e) => setCurrentItem(prev => ({ 
                            ...prev, 
                            individualDimensions: { 
                              ...prev.individualDimensions, 
                              width: e.target.value 
                            } 
                          }))}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Height"
                          value={currentItem.individualDimensions?.height || ''}
                          onChange={(e) => setCurrentItem(prev => ({ 
                            ...prev, 
                            individualDimensions: { 
                              ...prev.individualDimensions, 
                              height: e.target.value 
                            } 
                          }))}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                        <input
                          type="number"
                          placeholder="Weight (lbs)"
                          value={currentItem.individualDimensions?.weight || ''}
                          onChange={(e) => setCurrentItem(prev => ({ 
                            ...prev, 
                            individualDimensions: { 
                              ...prev.individualDimensions, 
                              weight: e.target.value 
                            } 
                          }))}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Multipack Details - placeholder for now */}
                {currentItem.packagingType === 'multipack' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                )}

                {/* Asset Tracking Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => openAssetModal('currentItem', 'serialNumbers')}
                    className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FiTag className="h-4 w-4 mr-2" />
                    Serial Numbers ({currentItem.serialNumbers.length})
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => openAssetModal('currentItem', 'macAddresses')}
                    className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FiWifi className="h-4 w-4 mr-2" />
                    MAC Addresses ({currentItem.macAddresses.length})
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Declared Value ($)
                    <span className="ml-1 relative group inline-flex align-middle">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Approximate value for insurance purposes
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={currentItem.declaredValue || ''}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, declaredValue: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Total value of this item line"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows="3"
                    value={currentItem.notes}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Any special notes for this item..."
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddItem(false);
                    setEditingItemIndex(null); // Clear editing state
                    setCurrentItem({
                      productSku: '',
                      internalSku: '',
                      productName: '',
                      packagingType: 'individual',
                      unitOfMeasure: 'each',
                      // For cartons
                      cartonCount: 1,
                      unitsPerCarton: 1,
                      // For individual items
                      quantity: 1,
                      // Consolidated dimensions field
                      dimensions: { length: '', width: '', height: '', weight: '' },
                      serialNumbers: [],
                      macAddresses: [],
                      notes: ''
                    });
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Validate required fields
                    if (!currentItem.productSku || !currentItem.productName) {
                      alert('Please fill in Product SKU and Name');
                      return;
                    }
                    
                    // Check for duplicate SKUs in current shipment (only when adding, not editing)
                    if (editingItemIndex === null && isDuplicateSku(currentItem.productSku)) {
                      const existsInInventory = searchResults.some(item => item.productSku === currentItem.productSku);
                      if (existsInInventory) {
                        alert(`SKU "${currentItem.productSku}" already exists in your inventory and is already added to this shipment. Please use the existing item or modify the quantity.`);
                      } else {
                        alert(`SKU "${currentItem.productSku}" is already added to this shipment. Please use a different SKU or modify the existing entry.`);
                      }
                      return;
                    }
                    
                    // Validate carton details if packaging type is carton
                    if (currentItem.packagingType === 'carton') {
                      if (!currentItem.cartonCount || currentItem.cartonCount < 1) {
                        alert('Please enter a valid number of cartons');
                        return;
                      }
                      if (!currentItem.unitsPerCarton || currentItem.unitsPerCarton < 1) {
                        alert('Please enter a valid number of units per carton');
                        return;
                      }
                    }
                    
                    // Validate individual quantity
                    if (currentItem.packagingType === 'individual' && (!currentItem.quantity || currentItem.quantity < 1)) {
                      alert('Please enter a valid quantity');
                      return;
                    }
                    
                    // Add or update item in current pallet
                    if (editingItemIndex !== null) {
                      // Update existing item
                      setCurrentPallet(prev => ({
                        ...prev,
                        items: prev.items.map((item, index) => 
                          index === editingItemIndex ? { ...currentItem } : item
                        )
                      }));
                    } else {
                      // Add new item
                      setCurrentPallet(prev => ({
                        ...prev,
                        items: [...prev.items, { ...currentItem, id: Date.now() }]
                      }));
                    }
                    
                    // Reset form and close modal
                    setCurrentItem({
                      productSku: '',
                      internalSku: '',
                      productName: '',
                      packagingType: 'individual',
                      unitOfMeasure: 'each',
                      // For cartons
                      cartonCount: 1,
                      unitsPerCarton: 1,
                      // For individual items
                      quantity: 1,
                      // Consolidated dimensions field
                      dimensions: { length: '', width: '', height: '', weight: '' },
                      serialNumbers: [],
                      macAddresses: [],
                      notes: ''
                    });
                    setSearchTerm('');
                    setSelectedProduct(null);
                    setShowProductDropdown(false);
                    setEditingItemIndex(null); // Clear editing state
                    setShowAddItem(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Special Instructions */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Receiving Service Instructions</label>
        <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-4">
          {[
            ['require_serial_numbers_on_receive', 'Record serial numbers on receipt'],
            ['require_mac_addresses_on_receive', 'Record MAC addresses on receipt'],
            ['relabel_on_receive', 'Relabel units on receipt']
          ].map(([key, label]) => (
            <label
              key={key}
              className={`flex cursor-pointer items-start rounded-md border px-3 py-3 text-sm transition-colors ${
                formik.values.receivingInstructions?.[key]
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={!!formik.values.receivingInstructions?.[key]}
                onChange={(e) => formik.setFieldValue(`receivingInstructions.${key}`, e.target.checked)}
                className="mt-0.5 mr-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="font-medium">{label}</div>
                <div className="text-xs text-gray-500">
                  Applies to this inbound request by default and is copied into any pallets or cartons you add.
                </div>
              </div>
            </label>
          ))}
          <p className="text-xs text-gray-500">
            These instructions apply to this inbound request only and can create receiving service charges when processed.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
        <textarea
          rows="4"
          value={formik.values.specialInstructions || ''}
          onChange={(e) => formik.setFieldValue('specialInstructions', e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder="Any special handling instructions or notes..."
        />
      </div>
      
      {/* Asset Modal for Serial Numbers and MAC Addresses */}
      {assetModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {assetModal.type === 'serialNumbers' ? 'Serial Numbers' : 'MAC Addresses'}
              </h3>
              
              <div className="space-y-4">
                {assetSaveMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                    {assetSaveMessage}
                  </div>
                )}

                {getTargetAssetValues(assetModal.target, assetModal.type).length > 0 && (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium text-green-800">
                        Added {formatAssetTypeLabel(assetModal.type)}
                      </div>
                      <button
                        type="button"
                        onClick={clearAssetValues}
                        className="text-xs font-medium text-green-700 hover:text-green-900"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {getTargetAssetValues(assetModal.target, assetModal.type).map((assetValue) => (
                        <button
                          key={assetValue}
                          type="button"
                          onClick={() => removeAssetValue(assetValue)}
                          className="group inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 hover:bg-green-200"
                          title="Remove"
                        >
                          <span>{assetValue}</span>
                          <span className="ml-2 hidden group-hover:inline">x</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Enter {assetModal.type === 'serialNumbers' ? 'serial numbers' : 'MAC addresses'} (one per line or comma-separated)
                  </label>
                  <textarea
                    rows="6"
                    value={assetModal.data}
                    onChange={(e) => {
                      setAssetModal(prev => ({ ...prev, data: e.target.value }));
                      setAssetSaveMessage('');
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder={assetModal.type === 'serialNumbers' 
                      ? 'SN001, SN002, SN003...' 
                      : '00:1B:44:11:3A:B7, 00:1B:44:11:3A:B8...'
                    }
                  />
                </div>
                
                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* CSV Import Option */}
                <div className="border-t pt-4">
                  <label className="text-sm font-medium text-gray-700">
                    Or import from CSV file
                  </label>
                  <p className="mt-1 text-xs text-amber-700">
                    CSV import replaces the current {formatAssetTypeLabel(assetModal.type).toLowerCase()} list.
                  </p>
                  <div className="space-y-2 mt-2">
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleCsvUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    {csvData && (
                      <button
                        type="button"
                        onClick={handleCsvReplace}
                        className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded"
                      >
                        Replace With CSV Data ({splitAssetValues(csvData).length} items)
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setAssetModal({ show: false, type: '', data: '', target: null });
                    setValidationErrors([]);
                    setAssetSaveMessage('');
                    setCsvFile(null);
                    setCsvData('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAssetModalSubmit}
                  disabled={isValidating}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isValidating ? 'Validating...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Carton Modal - MISSING MODAL IMPLEMENTATION */}
      {showAddCarton && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {currentCarton.id ? 'Edit Carton' : 'Add Carton'}
              </h3>
              
              <div className="space-y-6">
                {/* Product Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product SKU *</label>
                    <input
                      type="text"
                      value={currentCarton.productSku}
                      onChange={(e) => setCurrentCarton(prev => ({ ...prev, productSku: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter product SKU"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product Name *</label>
                    <input
                      type="text"
                      value={currentCarton.productName}
                      onChange={(e) => setCurrentCarton(prev => ({ ...prev, productName: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter product name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Internal SKU</label>
                  <input
                    type="text"
                    value={currentCarton.internalSku}
                    onChange={(e) => setCurrentCarton(prev => ({ ...prev, internalSku: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Internal part number (optional)"
                  />
                </div>

                {/* Carton Quantity and Unit of Measure */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Number of Cartons *</label>
                    <input
                      type="number"
                      min="1"
                      value={currentCarton.cartonCount || 1}
                      onChange={(e) => setCurrentCarton(prev => ({ 
                        ...prev, 
                        cartonCount: parseInt(e.target.value) || 1 
                      }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Units per Carton *</label>
                    <input
                      type="number"
                      min="1"
                      value={currentCarton.unitsPerCarton || 1}
                      onChange={(e) => setCurrentCarton(prev => ({ 
                        ...prev, 
                        unitsPerCarton: parseInt(e.target.value) || 1 
                      }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Carton Dimensions */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <h5 className="text-sm font-medium text-gray-800 mb-3">Carton Dimensions & Weight</h5>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Length (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.cartonDimensions?.length || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          cartonDimensions: { 
                            ...prev.cartonDimensions, 
                            length: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Width (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.cartonDimensions?.width || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          cartonDimensions: { 
                            ...prev.cartonDimensions, 
                            width: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Height (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.cartonDimensions?.height || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          cartonDimensions: { 
                            ...prev.cartonDimensions, 
                            height: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weight (lbs) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.cartonDimensions?.weight || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          cartonDimensions: { 
                            ...prev.cartonDimensions, 
                            weight: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Individual Item Dimensions */}
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <h5 className="text-sm font-medium text-gray-800 mb-3">Item Dimensions & Weight</h5>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Length (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.itemDimensions?.length || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          itemDimensions: { 
                            ...prev.itemDimensions, 
                            length: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Width (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.itemDimensions?.width || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          itemDimensions: { 
                            ...prev.itemDimensions, 
                            width: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Height (in) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.itemDimensions?.height || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          itemDimensions: { 
                            ...prev.itemDimensions, 
                            height: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weight (lbs) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentCarton.itemDimensions?.weight || ''}
                        onChange={(e) => setCurrentCarton(prev => ({ 
                          ...prev, 
                          itemDimensions: { 
                            ...prev.itemDimensions, 
                            weight: parseFloat(e.target.value) || 0 
                          } 
                        }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Unit of Measure */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Unit of Measure</label>
                  <select
                    value={currentCarton.unitOfMeasure}
                    onChange={(e) => setCurrentCarton(prev => ({ ...prev, unitOfMeasure: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="each">Each</option>
                    <option value="box">Box</option>
                    <option value="case">Case</option>
                    <option value="pack">Pack</option>
                  </select>
                </div>

                {/* Asset Tracking Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => openAssetModal('currentCarton', 'serialNumbers')}
                    className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FiTag className="h-4 w-4 mr-2" />
                    Serial Numbers ({(currentCarton.serialNumbers || []).length})
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => openAssetModal('currentCarton', 'macAddresses')}
                    className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FiWifi className="h-4 w-4 mr-2" />
                    MAC Addresses ({(currentCarton.macAddresses || []).length})
                  </button>
                </div>

                {/* Project Name / Code */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project Name</label>
                    <input
                      type="text"
                      value={currentCarton.projectName || ''}
                      onChange={(e) => setCurrentCarton(prev => ({ ...prev, projectName: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="e.g., Bank of Canada"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project Code</label>
                    <input
                      type="text"
                      value={currentCarton.projectCode || ''}
                      onChange={(e) => setCurrentCarton(prev => ({ ...prev, projectCode: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="e.g., BOC12345"
                    />
                  </div>
                </div>

                {/* Declared Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center">
                    Approx. Value ($)
                    <span className="relative group ml-1">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Approximate value for insurance purposes
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={currentCarton.declaredValue || ''}
                    onChange={(e) => setCurrentCarton(prev => ({ ...prev, declaredValue: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Total value of these cartons"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    rows="2"
                    value={currentCarton.notes || ''}
                    onChange={(e) => setCurrentCarton(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Any special notes for this carton..."
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCarton(false);
                    setCurrentCarton({
                      productSku: '',
                      internalSku: '',
                      productName: '',
                      cartonCount: 1,
                      unitsPerCarton: 1,
                      unitOfMeasure: 'each',
                      cartonDimensions: { length: '', width: '', height: '', weight: '' },
                      itemDimensions: { length: '', width: '', height: '', weight: '' },
                      serialNumbers: [],
                      macAddresses: [],
                      notes: '',
                      crossDock: false
                    });
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Validate required fields
                    if (!currentCarton.productSku || !currentCarton.productName) {
                      alert('Please fill in Product SKU and Name');
                      return;
                    }
                    
                    if (!currentCarton.cartonCount || currentCarton.cartonCount < 1) {
                      alert('Please enter a valid number of cartons');
                      return;
                    }
                    
                    if (!currentCarton.unitsPerCarton || currentCarton.unitsPerCarton < 1) {
                      alert('Please enter a valid number of units per carton');
                      return;
                    }

                    // Validate carton dimensions
                    const cartonDims = currentCarton.cartonDimensions || {};
                    if (!cartonDims.length || !cartonDims.width || !cartonDims.height || !cartonDims.weight) {
                      alert('Please fill in all carton dimensions and weight');
                      return;
                    }

                    // Validate item dimensions
                    const itemDims = currentCarton.itemDimensions || {};
                    if (!itemDims.length || !itemDims.width || !itemDims.height || !itemDims.weight) {
                      alert('Please fill in all individual item dimensions and weight');
                      return;
                    }
                    
                    // Calculate total quantity
                    const totalQuantity = currentCarton.cartonCount * currentCarton.unitsPerCarton;
                    
                    // Calculate total quantity
                    const cartonToAdd = {
                      ...currentCarton,
                      receivingInstructions: cloneReceivingInstructions(
                        currentCarton.receivingInstructions || formik.values.receivingInstructions
                      ),
                      quantity: totalQuantity,
                      id: currentCarton.id || Date.now() // Use existing ID if editing, or create new one
                    };
                    
                    // Check if we're editing an existing carton
                    const existingIndex = cartons.findIndex(c => c.id === cartonToAdd.id);
                    if (existingIndex >= 0) {
                      // Update existing carton
                      const updatedCartons = [...cartons];
                      updatedCartons[existingIndex] = cartonToAdd;
                      setCartons(updatedCartons);
                    } else {
                      // Add new carton
                      setCartons(prev => [...prev, cartonToAdd]);
                    }
                    
                    // Reset form and close modal
                    setCurrentCarton({
                      productSku: '',
                      internalSku: '',
                      productName: '',
                      cartonCount: 1,
                      unitsPerCarton: 1,
                      unitOfMeasure: 'each',
                      cartonDimensions: { length: '', width: '', height: '', weight: '' },
                      itemDimensions: { length: '', width: '', height: '', weight: '' },
                      serialNumbers: [],
                      macAddresses: [],
                      notes: '',
                      crossDock: false
                    });
                    setShowAddCarton(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {currentCarton.id ? 'Update Carton' : 'Add Carton'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default IncomingInventoryForm; 
